import type {ModelApplicationAcknowledgement} from './modelSnapshotApplication';
import {clearSessionTaskRecovery} from './sessionTaskRecoveryStorage';
import type {SessionModeDefinition} from './sessionModeDefinition';
// VivaAssess API Service
// Handles communication with the PostgreSQL backend

import { getEffectiveBcp47Locale } from '../i18n/locale';
import type { PlacementGeneratedQuestion } from '../services/placementQuestionAdapter';
import type { OralConversationStyle } from '../services/turnTakingPolicy';
import { normalizeApiBaseUrl } from './apiBaseUrl';
import type {SessionAuthoredDefinition} from './sessionAuthoredDefinition';
import type {SessionContextDefinition} from './sessionContextDefinition';
import type {SessionRetakeDefinition} from './sessionRetakeDefinition';

export { normalizeApiBaseUrl } from './apiBaseUrl';

function resolveApiUrl(): string {
  const configured = normalizeApiBaseUrl(String(import.meta.env.VITE_API_URL || ''));
  if (configured) {
    return configured;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    const devApiPort = String(import.meta.env.VITE_DEV_API_PORT || '3001').trim() || '3001';
    const isLocalHost = host === 'localhost' || host === '127.0.0.1';

    if (isLocalHost) {
      return `${protocol}//${host}:${devApiPort}`;
    }

    // Cloud default: assume API is served behind same-origin routing.
    return window.location.origin;
  }

  return 'http://localhost:3001';
}

const API_URL = resolveApiUrl();

// Assets (MCQ figures, uploads) are stored as API-relative paths so they stay
// portable across deployments; resolve them against the API origin for display.
export function resolveApiAssetUrl(url?: string | null): string {
  const value = String(url || '').trim();
  if (!value || /^(https?:|data:|blob:)/i.test(value)) return value;
  return `${API_URL}${value.startsWith('/') ? '' : '/'}${value}`;
}
const ACCESS_TOKEN_KEY = 'vivaassess_access_token';
const REFRESH_TOKEN_KEY = 'vivaassess_refresh_token';
const ACTIVE_GROUP_KEY = 'vivaassess_active_group_id';
export const API_ERROR_EVENT = 'vivaassess:api-error';

export interface ApiErrorEventDetail {
  status: number;
  code?: string;
  message: string;
  requestId?: string | null;
  endpoint: string;
  navigateToNotFound?: boolean;
}

interface ApiRequestOptions extends RequestInit {
  suppressGlobalErrorRedirect?: boolean;
  navigateOnNotFound?: boolean;
}

interface ParsedApiError {
  status: number;
  code: string;
  message: string;
  requestId: string | null;
  retryAfterMs: number | null;
  details?: unknown;
}

export class ApiRequestError extends Error {
  status: number;
  code: string;
  requestId: string | null;
  retryAfterMs: number | null;
  endpoint: string;
  details?: unknown;

  constructor(parsedError: ParsedApiError, endpoint: string) {
    super(parsedError.message);
    this.name = 'ApiRequestError';
    this.status = parsedError.status;
    this.code = parsedError.code;
    this.requestId = parsedError.requestId;
    this.retryAfterMs = parsedError.retryAfterMs;
    this.endpoint = endpoint;
    this.details = parsedError.details;
  }
}

function statusToDefaultCode(status: number): string {
  if (status === 400) return 'BAD_REQUEST';
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 503) return 'SERVICE_UNAVAILABLE';
  if (status >= 500) return 'INTERNAL_SERVER_ERROR';
  return 'REQUEST_FAILED';
}

function parseRetryAfterMs(value: string | null): number | null {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const numericSeconds = Number(trimmed);
  if (Number.isFinite(numericSeconds) && numericSeconds >= 0) {
    return Math.round(numericSeconds * 1000);
  }

  const asDate = Date.parse(trimmed);
  if (!Number.isNaN(asDate)) {
    return Math.max(0, asDate - Date.now());
  }

  return null;
}

function parseRateLimitResetMs(value: string | null): number | null {
  if (!value) return null;

  const seconds = Number(value.trim());
  if (!Number.isFinite(seconds) || seconds < 0) {
    return null;
  }

  return Math.round(seconds * 1000);
}

function resolveRetryAfterMs(headers: Headers): number | null {
  const retryAfterMs = parseRetryAfterMs(headers.get('retry-after'));
  if (retryAfterMs !== null) {
    return retryAfterMs;
  }

  return parseRateLimitResetMs(headers.get('ratelimit-reset'));
}

function emitApiErrorEvent(detail: ApiErrorEventDetail): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent<ApiErrorEventDetail>(API_ERROR_EVENT, { detail }));
}

function shouldEmitGlobalError(status: number, options: Pick<ApiRequestOptions, 'suppressGlobalErrorRedirect' | 'navigateOnNotFound'>): boolean {
  if (options.suppressGlobalErrorRedirect) return false;
  if (status === 401 || status === 403) return true;
  if (status === 404) return Boolean(options.navigateOnNotFound);
  if (status >= 500) return true;
  return false;
}

async function parseApiErrorResponse(response: Response, fallbackMessage: string): Promise<ParsedApiError> {
  const requestId = response.headers.get('x-request-id');

  let payload: Record<string, unknown> | null = null;
  try {
    payload = (await response.clone().json()) as Record<string, unknown>;
  } catch {
    payload = null;
  }

  let fallbackText = '';
  if (!payload) {
    try {
      fallbackText = (await response.clone().text()).trim();
    } catch {
      fallbackText = '';
    }
  }

  const status = response.status || 500;
  const message =
    (typeof payload?.message === 'string' && payload.message)
    || (typeof payload?.error === 'string' && payload.error)
    || fallbackText
    || fallbackMessage;

  const code =
    (typeof payload?.code === 'string' && payload.code)
    || statusToDefaultCode(status);

  const retryAfterMs = status === 429
    ? resolveRetryAfterMs(response.headers)
    : null;

  const payloadRequestId = typeof payload?.requestId === 'string' ? payload.requestId : null;

  return {
    status,
    code,
    message,
    requestId: payloadRequestId || requestId,
    retryAfterMs,
    details: payload?.details
  };
}

async function throwApiErrorFromResponse(
  response: Response,
  endpoint: string,
  fallbackMessage: string,
  options: Pick<ApiRequestOptions, 'suppressGlobalErrorRedirect' | 'navigateOnNotFound'> = {}
): Promise<never> {
  const parsed = await parseApiErrorResponse(response, fallbackMessage);

  if (shouldEmitGlobalError(parsed.status, options)) {
    emitApiErrorEvent({
      status: parsed.status,
      code: parsed.code,
      message: parsed.message,
      requestId: parsed.requestId,
      endpoint,
      navigateToNotFound: parsed.status === 404 && Boolean(options.navigateOnNotFound)
    });
  }

  throw new ApiRequestError(parsed, endpoint);
}

// Token management
let accessToken: string | null = localStorage.getItem(ACCESS_TOKEN_KEY);
let refreshToken: string | null = localStorage.getItem(REFRESH_TOKEN_KEY);
let activeGroupId: string | null = localStorage.getItem(ACTIVE_GROUP_KEY);

// Token setters
export function setTokens(access: string, refresh: string): void {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

export function setActiveGroupId(groupId: string | null): void {
  const normalized = String(groupId || '').trim();
  if (!normalized) {
    activeGroupId = null;
    localStorage.removeItem(ACTIVE_GROUP_KEY);
    return;
  }

  activeGroupId = normalized;
  localStorage.setItem(ACTIVE_GROUP_KEY, normalized);
}

export function getActiveGroupId(): string | null {
  return activeGroupId;
}

export function clearTokens(): void {
  clearSessionTaskRecovery();
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  setActiveGroupId(null);
}

export function getAccessToken(): string | null {
  return accessToken;
}

function applyLocaleHeader(headers: Record<string, string>): void {
  headers['x-locale'] = getEffectiveBcp47Locale();
}

// Request helper for file uploads (multipart/form-data — no Content-Type header)
async function apiUpload<T>(
  endpoint: string,
  formData: FormData,
  options: Omit<ApiRequestOptions, 'body'> = {}
): Promise<T> {
  const {
    suppressGlobalErrorRedirect,
    navigateOnNotFound,
    ...requestInit
  } = options;

  const url = `${API_URL}/api${endpoint}`;

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  if (activeGroupId) {
    headers['x-group-id'] = activeGroupId;
  }
  applyLocaleHeader(headers);

  let response = await fetch(url, {
    ...requestInit,
    method: 'POST',
    headers,
    body: formData,
  });

  // If unauthorized and we have a refresh token, try to refresh
  if (response.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      response = await fetch(url, {
        ...requestInit,
        method: 'POST',
        headers,
        body: formData
      });
    }
  }

  if (!response.ok) {
    await throwApiErrorFromResponse(response, endpoint, 'Upload failed', {
      suppressGlobalErrorRedirect,
      navigateOnNotFound
    });
  }

  return response.json();
}

// Request helper for binary downloads (e.g. PDF reports)
async function apiRequestBlob(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<Blob> {
  const {
    suppressGlobalErrorRedirect,
    navigateOnNotFound,
    ...requestInit
  } = options;

  const url = `${API_URL}/api${endpoint}`;

  const headers: HeadersInit = {
    ...(requestInit.headers || {}),
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }
  if (activeGroupId) {
    (headers as Record<string, string>)['x-group-id'] = activeGroupId;
  }
  applyLocaleHeader(headers as Record<string, string>);

  let response = await fetch(url, {
    ...requestInit,
    headers,
  });

  if (response.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
      response = await fetch(url, { ...requestInit, headers });
    }
  }

  if (!response.ok) {
    await throwApiErrorFromResponse(response, endpoint, 'Request failed', {
      suppressGlobalErrorRedirect,
      navigateOnNotFound
    });
  }

  return response.blob();
}

// Request helper with automatic token refresh
async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const {
    suppressGlobalErrorRedirect,
    navigateOnNotFound,
    ...requestInit
  } = options;

  const url = `${API_URL}/api${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(requestInit.headers || {}),
  };

  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }
  if (activeGroupId) {
    (headers as Record<string, string>)['x-group-id'] = activeGroupId;
  }
  applyLocaleHeader(headers as Record<string, string>);

  let response = await fetch(url, {
    ...requestInit,
    headers,
  });

  // If unauthorized and we have a refresh token, try to refresh
  if (response.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry with new token
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
      response = await fetch(url, { ...requestInit, headers });
    }
  }

  if (!response.ok) {
    await throwApiErrorFromResponse(response, endpoint, 'Request failed', {
      suppressGlobalErrorRedirect,
      navigateOnNotFound
    });
  }

  return response.json();
}

// Refresh access token
async function refreshAccessToken(): Promise<boolean> {
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-locale': getEffectiveBcp47Locale()
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      return false;
    }

    const data = await response.json();
    setTokens(data.accessToken, data.refreshToken);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

// =============================================================================
// AUTH API
// =============================================================================
export interface LoginResponse {
  user: UserProfile;
  accessToken: string;
  refreshToken: string;
}

export interface CandidateAssignmentSummary {
  id: string;
  candidate_id: string;
  interview_id: string;
  interview_scheduled_at?: string;
  interview_completion_deadline?: string;
  assignment_status: string;
  interview_type?: 'text' | 'voice' | 'mcq' | string;
  candidate_can_view_results?: boolean;
  auto_approve_assessment?: boolean;
  review_mode?: 'manual' | 'auto_approve' | 'auto_review' | string;
  proctoring_enabled?: boolean;
  proctoring_config?: Record<string, any>;
  proctoring_require_consent?: boolean;
  proctoring_evidence_mode?: 'metadata_only' | 'extended' | string;
  restriction_enabled?: boolean;
  allowed_ip_ranges?: string[];
  allowed_locations?: Array<{
    raw_input: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    normalized_tokens?: string[];
  }>;
  match_mode?: 'any' | 'all' | string;
  enforcement_mode?: 'start_only' | 'continuous' | string;
  fail_action?: 'block' | 'warn' | string;
  allow_retake?: boolean;
  retake_scope?: 'none' | 'part_a' | 'part_b' | 'both' | string;
  max_attempts?: number;
  attempt_count?: number;
  assignment_created_at?: string;
  assignment_updated_at?: string;
  interview_title?: string;
  available_from?: string | null;
  available_until?: string | null;
}

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  neptun_code?: string | null;
  avatar_url?: string | null;
  role: 'super_admin' | 'admin' | 'candidate' | 'invigilator';
  primary_group_id?: string | null;
  active_group_id?: string | null;
  username?: string;
  status?: string;
  password_changed?: boolean;
  has_invitation?: boolean;
  invitation_expiry?: string | null;
  invitation_expired?: boolean;
  created_at?: string;
  updated_at?: string;
  timezone?: string;
  interview_id?: string;
  interview_scheduled_at?: string;
  interview_completion_deadline?: string;
  assignment_status?: string;
  interview_type?: 'text' | 'voice' | 'mcq' | string;
  candidate_can_view_results?: boolean;
  auto_approve_assessment?: boolean;
  review_mode?: 'manual' | 'auto_approve' | 'auto_review' | string;
  proctoring_enabled?: boolean;
  proctoring_config?: Record<string, any>;
  proctoring_require_consent?: boolean;
  proctoring_evidence_mode?: 'metadata_only' | 'extended' | string;
  restriction_enabled?: boolean;
  allowed_ip_ranges?: string[];
  allowed_locations?: Array<{
    raw_input: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    normalized_tokens?: string[];
  }>;
  match_mode?: 'any' | 'all' | string;
  enforcement_mode?: 'start_only' | 'continuous' | string;
  fail_action?: 'block' | 'warn' | string;
  allow_retake?: boolean;
  max_attempts?: number;
  attempt_count?: number;
  assignment_updated_at?: string;
  interview_title?: string;
  candidate_assignments?: CandidateAssignmentSummary[];
}

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-locale': getEffectiveBcp47Locale()
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      await throwApiErrorFromResponse(response, '/auth/login', 'Invalid credentials', {
        suppressGlobalErrorRedirect: true
      });
    }

    const data = await response.json();
    setTokens(data.accessToken, data.refreshToken);
    setActiveGroupId(data?.user?.active_group_id || null);
    return data;
  },

  logout: async (): Promise<void> => {
    try {
      await apiRequest('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } finally {
      clearTokens();
    }
  },

  getProfile: async (): Promise<{ user: UserProfile }> => {
    const profile = await apiRequest<{ user: UserProfile }>('/auth/me');
    setActiveGroupId(profile.user?.active_group_id || null);
    return profile;
  },

  register: async (data: {
    email: string;
    password: string;
    full_name: string;
    role?: string;
  }): Promise<LoginResponse> => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-locale': getEffectiveBcp47Locale()
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await throwApiErrorFromResponse(response, '/auth/register', 'Registration failed', {
        suppressGlobalErrorRedirect: true
      });
    }

    const respData = await response.json();
    setTokens(respData.accessToken, respData.refreshToken);
    setActiveGroupId(respData?.user?.active_group_id || null);
    return respData;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  verifyInvitation: async (token: string): Promise<{ valid: boolean; email: string; full_name: string }> => {
    const response = await fetch(`${API_URL}/api/auth/verify-invitation/${token}`, {
      headers: {
        'x-locale': getEffectiveBcp47Locale()
      }
    });
    
    if (!response.ok) {
      await throwApiErrorFromResponse(response, `/auth/verify-invitation/${token}`, 'Invalid invitation', {
        suppressGlobalErrorRedirect: true
      });
    }
    
    return response.json();
  },

  acceptInvitation: async (token: string, password: string): Promise<LoginResponse> => {
    const response = await fetch(`${API_URL}/api/auth/accept-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-locale': getEffectiveBcp47Locale()
      },
      body: JSON.stringify({ token, password }),
    });

    if (!response.ok) {
      await throwApiErrorFromResponse(response, '/auth/accept-invitation', 'Failed to set password', {
        suppressGlobalErrorRedirect: true
      });
    }

    const data = await response.json();
    setTokens(data.accessToken, data.refreshToken);
    setActiveGroupId(data?.user?.active_group_id || null);
    return data;
  },
};

// =============================================================================
// GROUPS API
// =============================================================================
export type GroupType = 'institution' | 'enterprise' | 'department' | 'faculty' | 'unit';
export type GroupStatus = 'active' | 'inactive';
export type MembershipStatus = 'active' | 'inactive' | 'pending';

export interface GroupMembership {
  id: string;
  user_id: string;
  group_id: string;
  role: UserProfile['role'];
  status: MembershipStatus;
  created_at?: string;
  updated_at?: string;
  group_name?: string;
  group_slug?: string;
  group_type?: GroupType;
  parent_group_id?: string | null;
  group_status?: GroupStatus;
}

export interface GroupRecord {
  id: string;
  name: string;
  slug: string;
  type: GroupType;
  parent_group_id?: string | null;
  status: GroupStatus;
  metadata?: Record<string, any>;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by_name?: string | null;
  active_member_count?: number;
  children?: GroupRecord[];
}

export interface GroupUserMembership {
  id: string;
  user_id: string;
  group_id: string;
  role: UserProfile['role'];
  status: MembershipStatus;
  created_at?: string;
  updated_at?: string;
  email?: string;
  full_name?: string;
  username?: string;
  user_status?: string;
}

export interface GroupAssignableUser {
  id: string;
  email: string;
  full_name: string;
  role: UserProfile['role'];
  username?: string;
  status?: string;
  primary_group_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface GroupMembershipOverview {
  membership_id: string;
  user_id: string;
  group_id: string;
  membership_role: UserProfile['role'];
  membership_status: MembershipStatus;
  membership_created_at?: string;
  membership_updated_at?: string;
  full_name: string;
  email: string;
  username?: string;
  user_role: UserProfile['role'];
  user_status?: string;
  group_name: string;
  group_type: GroupType;
  group_status?: GroupStatus;
  parent_group_id?: string | null;
  parent_group_name?: string | null;
  parent_group_type?: GroupType | null;
}

export interface GroupBulkAssignSummary {
  group_id: string;
  role: UserProfile['role'];
  status: MembershipStatus;
  overwrite_existing: boolean;
  requested_count: number;
  processed_count: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  invalid_ids: string[];
  missing_users: string[];
}

export const groupsApi = {
  getMy: async () => {
    const result = await apiRequest<{
      active_group_id: string | null;
      memberships: GroupMembership[];
    }>('/groups/me');

    setActiveGroupId(result.active_group_id || null);
    return result;
  },

  setActiveGroup: async (groupId: string) => {
    const response = await apiRequest<{
      message: string;
      active_group_id: string;
    }>('/groups/me/active-group', {
      method: 'PATCH',
      body: JSON.stringify({ group_id: groupId })
    });

    setActiveGroupId(response.active_group_id || groupId);
    return response;
  },

  list: async (params?: { include_inactive?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (typeof params?.include_inactive === 'boolean') {
      queryParams.set('include_inactive', String(params.include_inactive));
    }

    const query = queryParams.toString();
    return apiRequest<{ groups: GroupRecord[]; tree: GroupRecord[] }>(
      `/groups${query ? `?${query}` : ''}`
    );
  },

  create: async (data: {
    name: string;
    slug?: string;
    type: GroupType;
    parent_group_id?: string | null;
    status?: GroupStatus;
    metadata?: Record<string, any>;
  }) => {
    return apiRequest<{ group: GroupRecord }>('/groups', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  update: async (groupId: string, data: {
    name?: string;
    slug?: string;
    type?: GroupType;
    metadata?: Record<string, any>;
  }) => {
    return apiRequest<{ group: GroupRecord }>(`/groups/${groupId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  updateParent: async (groupId: string, parent_group_id: string | null) => {
    return apiRequest<{ group: GroupRecord }>(`/groups/${groupId}/parent`, {
      method: 'PATCH',
      body: JSON.stringify({ parent_group_id })
    });
  },

  updateStatus: async (groupId: string, status: GroupStatus) => {
    return apiRequest<{ group: GroupRecord }>(`/groups/${groupId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  },

  listUsers: async (groupId: string) => {
    return apiRequest<{ users: GroupUserMembership[] }>(`/groups/${groupId}/users`);
  },

  assignUser: async (groupId: string, data: {
    user_id: string;
    role: UserProfile['role'];
    status?: MembershipStatus;
  }) => {
    return apiRequest<{ membership: GroupMembership }>(`/groups/${groupId}/users`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  assignUsersBulk: async (groupId: string, data: {
    user_ids: string[];
    role: UserProfile['role'];
    status?: MembershipStatus;
    overwrite_existing?: boolean;
  }) => {
    return apiRequest<{ summary: GroupBulkAssignSummary }>(`/groups/${groupId}/users/bulk`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  moveMembership: async (membershipId: string, data: {
    target_group_id: string;
    keep_source_membership?: boolean;
    role?: UserProfile['role'];
    status?: MembershipStatus;
  }) => {
    return apiRequest<{
      membership: GroupMembership;
      source_membership_id: string;
      source_membership_removed: boolean;
      mode: 'move' | 'add';
    }>(`/groups/memberships/${membershipId}/move`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateMembership: async (membershipId: string, data: {
    role?: UserProfile['role'];
    status?: MembershipStatus;
  }) => {
    return apiRequest<{ membership: GroupMembership }>(`/groups/memberships/${membershipId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  removeMembership: async (membershipId: string) => {
    return apiRequest<{ message: string }>(`/groups/memberships/${membershipId}`, {
      method: 'DELETE'
    });
  },

  listAssignableUsers: async (params?: {
    search?: string;
    role?: UserProfile['role'];
    status?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.role) queryParams.set('role', params.role);
    if (params?.status) queryParams.set('status', params.status);
    if (typeof params?.limit === 'number') queryParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') queryParams.set('offset', String(params.offset));

    const query = queryParams.toString();
    return apiRequest<{
      users: GroupAssignableUser[];
      total: number;
      limit: number;
      offset: number;
    }>(`/groups/users${query ? `?${query}` : ''}`);
  },

  listMembershipOverview: async (params?: { include_inactive?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (typeof params?.include_inactive === 'boolean') {
      queryParams.set('include_inactive', String(params.include_inactive));
    }

    const query = queryParams.toString();
    return apiRequest<{ memberships: GroupMembershipOverview[] }>(
      `/groups/memberships/overview${query ? `?${query}` : ''}`
    );
  },

  listUserGroups: async (userId: string) => {
    return apiRequest<{ user: UserProfile; memberships: GroupMembership[] }>(`/groups/users/${userId}/groups`);
  }
};

// =============================================================================
// LLM CONFIG API
// =============================================================================
export type LLMImageProvider = 'openai' | 'foundry' | 'azure_openai';

export interface LLMImageConfigResponse {
  enabled: boolean;
  provider: string;
  api_endpoint?: string;
  model: string;
  size: string;
  quality: string;
  timeout_ms: number;
  has_api_key: boolean;
  is_configured?: boolean;
  error?: string | null;
}

export interface LLMConfigResponse {
  provider: string;
  api_endpoint?: string;
  model: string;
  max_tokens: number;
  temperature: number;
  timeout_ms: number;
  has_api_key: boolean;
  is_configured?: boolean;
  image?: LLMImageConfigResponse;
  auto_review?: {
    pass_threshold: number;
    min_confidence: number;
    escalate_on_integrity: boolean;
    stuck_minutes: number;
    max_tokens: number;
    max_in_flight: number;
  };
}

export interface LLMTestResponse {
  connected: boolean;
  provider: string;
  endpoint?: string;
  model?: string | null;
  model_available?: boolean;
  available_models?: string[];
  note?: string;
  error?: string;
}

export interface LLMImageTestResponse {
  connected: boolean;
  provider: string;
  endpoint?: string;
  model?: string | null;
  asset_url?: string;
  note?: string;
  error?: string;
}

export interface LLMStatusResponse {
  connected: boolean;
  provider: string;
  endpoint?: string;
  model?: string;
  model_available?: boolean;
  available_models?: string[];
  note?: string;
  error?: string;
}

export type SessionTaskPurpose='interview_start'|'interview_next_question'|'interview_response_advice'|'interview_followup';
export type SessionTaskResponse={ response: string; json_response?: string; provider?: string; model?: string;
      task_receipt?:{request_id:string;replayed:boolean};
      task_definition?:{response_version?:number|null;dialogue_version?:number|null;purpose:'interview_start'|'interview_next_question'|'interview_response_advice'|'interview_followup';version:number|null;overridden:boolean;template_hash:string|null;session_id:string;prompt_hash:string;request_size:number;server_rendered:boolean;template_id:string|null;template_version:string|null};
      opening_definition?:{version:number|null;overridden:boolean;template_hash:string|null;session_id:string;prompt_hash:string;request_size:number} };
export type SessionTaskRecovery={state:'pending';session_id:string;request_id:string;purpose:SessionTaskPurpose;retry_after_seconds:number}
  | {state:'accepted';session_id:string;request_id:string;purpose:SessionTaskPurpose;response:SessionTaskResponse;
    snapshot_application?:{version:1;current_revision:number;first_revision:number|null;checkpoint:{version:1;base_revision:number;question_number:number}|null}};

export const llmConfigApi = {
  recoverSessionTask: async(sessionId:string,purpose:SessionTaskPurpose,requestId:string,options:{signal?:AbortSignal}={})=>apiRequest<SessionTaskRecovery>(
    `/llm/session-tasks/${encodeURIComponent(sessionId)}/${encodeURIComponent(purpose)}/${encodeURIComponent(requestId)}`,
    {suppressGlobalErrorRedirect:true,signal:options.signal}),
  chat: async (data: { request_id?:string;prompt: string; systemPrompt?: string; purpose?:'interview_start'|'interview_next_question'|'interview_response_advice'|'interview_followup';expected_response_version?:number;response_turn?:{question:string;response:string;response_time_seconds?:number;current_depth?:number};expected_dialogue_version?:number;session_id?:string;interview_mode?:'job_interview'|'school_admission';turn?:{previous_question:string;previous_response:string;response_quality:string;question_number:number;total_questions:number} },options:{signal?:AbortSignal}={}) => {
    return apiRequest<SessionTaskResponse>('/llm/chat', {
      method: 'POST', body: JSON.stringify(data), suppressGlobalErrorRedirect: true, signal:options.signal
    });
  },
  get: async () => {
    return apiRequest<{ config: LLMConfigResponse }>('/llm/config');
  },

  update: async (data: {
    provider: string;
    api_endpoint?: string;
    model: string;
    max_tokens?: number;
    temperature?: number;
    timeout_ms?: number;
    api_key?: string;
    image?: {
      enabled: boolean;
      provider?: string;
      api_endpoint?: string;
      model?: string;
      size?: string;
      quality?: string;
      timeout_ms?: number;
      api_key?: string;
    };
    auto_review?: {
      pass_threshold?: number;
      min_confidence?: number;
      escalate_on_integrity?: boolean;
      stuck_minutes?: number;
      max_tokens?: number;
      max_in_flight?: number;
    };
  }) => {
    return apiRequest<{ config: LLMConfigResponse }>('/llm/config', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  test: async (data: {
    provider: string;
    api_endpoint?: string;
    model?: string;
    timeout_ms?: number;
    api_key?: string;
  }) => {
    return apiRequest<LLMTestResponse>('/llm/test', {
      method: 'POST',
      body: JSON.stringify(data),
      suppressGlobalErrorRedirect: true
    });
  },

  testImage: async (data: {
    provider: string;
    api_endpoint?: string;
    model?: string;
    size?: string;
    quality?: string;
    timeout_ms?: number;
    api_key?: string;
  }) => {
    return apiRequest<LLMImageTestResponse>('/llm/test-image', {
      method: 'POST',
      body: JSON.stringify(data),
      suppressGlobalErrorRedirect: true
    });
  },

  status: async () => {
    return apiRequest<LLMStatusResponse>('/llm/status');
  },

  reset: async (options?: { clear_api_keys?: boolean }) => {
    return apiRequest<{ config: LLMConfigResponse; message?: string }>('/llm/config/reset', {
      method: 'POST',
      body: JSON.stringify({ clear_api_keys: Boolean(options?.clear_api_keys) })
    });
  }
};

// =============================================================================
// USERS API
// =============================================================================
export const usersApi = {
  list: async (params?: { role?: string; status?: string; limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();

    if (params?.role) queryParams.set('role', params.role);
    if (params?.status) queryParams.set('status', params.status);
    if (typeof params?.limit === 'number') queryParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') queryParams.set('offset', String(params.offset));

    const query = queryParams.toString();
    return apiRequest<{ users: UserProfile[]; total: number }>(`/users${query ? `?${query}` : ''}`);
  },

  listAll: async (params?: { role?: string; status?: string }) => {
    const pageSize = 500;
    const users: UserProfile[] = [];
    let offset = 0;
    let total = Number.POSITIVE_INFINITY;

    while (offset < total) {
      const page = await usersApi.list({
        ...params,
        limit: pageSize,
        offset
      });
      const batch = page.users || [];
      total = typeof page.total === 'number' ? page.total : offset + batch.length;
      users.push(...batch);
      if (batch.length === 0 || batch.length < pageSize) {
        break;
      }
      offset += batch.length;
    }

    return { users, total: users.length };
  },

  get: async (id: string) => {
    return apiRequest<{ user: UserProfile }>(`/users/${id}`);
  },

  create: async (data: { email: string; full_name: string; role?: string; password?: string; neptun_code?: string | null }) => {
    return apiRequest<{
      user: UserProfile;
      invitationToken?: string;
      invitationExpiry?: string;
      invitation_link?: string;
    }>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getInvitation: async (userId: string) => {
    return apiRequest<{
      user_id: string;
      email: string;
      full_name: string;
      status: string;
      role: string;
      invitation_token: string;
      invitation_link: string | null;
      invitation_expiry: string | null;
      expired: boolean;
    }>(`/users/${userId}/invitation`);
  },

  resendInvitation: async (userId: string) => {
    return apiRequest<{
      message: string;
      invitationToken: string;
      invitationExpiry: string;
      invitation_link?: string | null;
    }>(
      `/users/${userId}/resend-invitation`,
      { method: 'POST' }
    );
  },

  revokeInvitation: async (userId: string) => {
    return apiRequest<{
      message: string;
      user_id: string;
      has_invitation: boolean;
      invitation_expiry: null;
      invitation_expired: boolean;
    }>(`/users/${userId}/invitation/revoke`, {
      method: 'POST'
    });
  },

  resetPassword: async (
    userId: string,
    data: { password: string; force_change?: boolean }
  ) => {
    return apiRequest<{
      message: string;
      user: UserProfile;
      note?: string;
    }>(`/users/${userId}/password`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  update: async (id: string, data: { full_name?: string; username?: string; status?: string; role?: string; neptun_code?: string | null }) => {
    return apiRequest<{ user: UserProfile }>(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  uploadAvatar: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);

    return apiUpload<{ message: string; user: UserProfile }>(`/users/${id}/avatar`, formData, {
      method: 'POST', suppressGlobalErrorRedirect: true
    });
  },

  deleteAvatar: async (id: string) => {
    return apiRequest<{ message: string; user: UserProfile }>(`/users/${id}/avatar`, {
      method: 'DELETE', suppressGlobalErrorRedirect: true
    });
  },

  updateResultVisibility: async (
    candidateId: string,
    data: { candidate_can_view_results: boolean; interview_id?: string }
  ) => {
    return apiRequest<{
      assignment: {
        id: string;
        candidate_id: string;
        interview_id: string;
        status: string;
        candidate_can_view_results: boolean;
        updated_at: string;
      };
    }>(`/users/${candidateId}/result-visibility`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  cancelAssignment: async (candidateId: string, assignmentId: string) => {
    return apiRequest<{ assignment: CandidateAssignmentSummary }>(
      `/users/${candidateId}/assignments/${assignmentId}/cancel`,
      { method: 'PATCH' }
    );
  },

  delete: async (id: string) => {
    return apiRequest<{ success: false; erased: false; erasure_requested: true; reused: boolean;
      code: 'PRIVACY_ERASURE_REVIEW_REQUESTED'; privacy_center_path: string; request: PrivacyRequestRow
    }>(`/users/${id}`, { method: 'DELETE', body: JSON.stringify({ confirm: true }), suppressGlobalErrorRedirect: true });
  },
};

// =============================================================================
// INTERVIEWS API
// =============================================================================
export interface Interview {
  assignment_readiness?: { can_assign: boolean; blocking_reasons: string[]; oral_source: string };
  id: string;
  title: string;
  description?: string;
  estimated_duration_minutes: number;
  /** Part B (oral) session budget. Independent of Part A MCQ time. Null = legacy fallback to estimated_duration_minutes. */
  part_b_duration_minutes?: number | null;
  max_candidates?: number;
  focus_areas?: string[];
  difficulty_level: string;
  language: string;
  /** Spoken examiner identity for greetings. Not the Voice Studio voice label. */
  ai_interviewer_name?: string | null;
  instructions?: string;
  status: string;
  proctoring_enabled?: boolean;
  proctoring_config?: Record<string, any>;
  proctoring_require_consent?: boolean;
  proctoring_evidence_mode?: 'metadata_only' | 'extended' | string;
  assignment_proctoring_enabled?: boolean;
  assignment_proctoring_config?: Record<string, any>;
  created_at: string;
  candidate_count?: number;
  assignment_completion_deadline?: string;
  // Cohort-wide assessment window (rebuilt scheduling): when set, ALL
  // candidates assigned to this interview may only start their session
  // within [available_from, available_until]. Distinct from the per-
  // assignment scheduled_at/completion_deadline used for individual slots.
  available_from?: string | null;
  available_until?: string | null;
  placement_id?: string | null;
  /** Joined from placements when placement_id is set (list/get readiness). */
  placement_name?: string | null;
  placement_status?: string | null;
  placement_template_id?: string | null;
  placement_template_active?: boolean;
  part_a_enabled?: boolean;
  part_b_enabled?: boolean;
  assessment_mode?: 'hybrid' | 'part_a' | 'part_b';
  authored_oral_question_count?: number;
  mcq_settings_present?: boolean;
  /**
   * Part B conversation tuning (barge-in, acknowledgements, adaptive silence).
   * Null/absent means the client uses DEFAULT_CONVERSATION_STYLE.
   */
  oral_conversation_style?: Partial<OralConversationStyle> | null;
  questions?: InterviewQuestion[];
  candidate_can_view_results?: boolean;
  restriction_enabled?: boolean;
  allowed_ip_ranges?: string[];
  allowed_locations?: Array<{
    raw_input: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    normalized_tokens?: string[];
  }>;
  match_mode?: 'any' | 'all' | string;
  enforcement_mode?: 'start_only' | 'continuous' | string;
  fail_action?: 'block' | 'warn' | string;
  group_id?: string;
  group_name?: string;
  group_type?: GroupType;
  parent_group_id?: string | null;
  parent_group_name?: string | null;
  parent_group_type?: GroupType | null;
  prompt_template?: string|null;
}

export interface InterviewQuestion {
  id?: string;
  interview_id?: string;
  question_text: string;
  question_type?: 'open' | 'technical' | 'behavioral' | 'situational' | 'follow_up';
  expected_duration_seconds?: number;
  difficulty_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  evaluation_criteria?: Record<string, any>;
  order_index?: number;
  is_required?: boolean;
  created_at?: string;
}

export const interviewsApi = {
  list: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest<{ interviews: Interview[] }>(`/interviews?${query}`);
  },

  get: async (id: string) => {
    return apiRequest<{ interview: Interview; questions: InterviewQuestion[] }>(`/interviews/${id}`);
  },

  create: async (data: Partial<Interview> & { questions?: Partial<InterviewQuestion>[] }) => {
    return apiRequest<{ interview: Interview }>('/interviews', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: string, data: Partial<Interview> & { questions?: Partial<InterviewQuestion>[] }) => {
    return apiRequest<{ interview: Interview }>(`/interviews/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  resetProctoring: async (id: string) => {
    return apiRequest<{
      interview: Interview;
      defaults: Record<string, unknown>;
      message?: string;
    }>(`/interviews/${id}/proctoring/reset`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  delete: async (id: string) => {
    return apiRequest<{ message: string }>(`/interviews/${id}`, { method: 'DELETE' });
  },

  assign: async (
    interviewId: string,
    candidateId: string,
    scheduledAt?: string,
    interviewType: 'text' | 'voice' | 'mcq' = 'voice',
    allowRetake: boolean = false,
    maxAttempts: number = 1,
    completionDeadline?: string | null,
    candidateCanViewResults: boolean = false,
    autoApproveAssessment: boolean = false,
    accessPolicy?: {
      restriction_enabled?: boolean;
      allowed_ip_ranges?: string[];
      allowed_locations?: Array<{
        raw_input: string;
        city?: string | null;
        region?: string | null;
        country?: string | null;
        normalized_tokens?: string[];
      }>;
      match_mode?: 'any' | 'all';
      enforcement_mode?: 'start_only' | 'continuous';
      fail_action?: 'block' | 'warn';
    },
    proctoringPolicy?: {
      proctoring_enabled?: boolean;
      proctoring_config?: Record<string, any>;
    },
    retakeScope?: 'none' | 'part_a' | 'part_b' | 'both',
    reviewMode?: 'manual' | 'auto_approve' | 'auto_review'
  ) => {
    const payload = {
      candidate_id: candidateId,
      scheduled_at: scheduledAt,
      completion_deadline: completionDeadline ?? null,
      interview_type: interviewType,
      allow_retake: allowRetake,
      max_attempts: maxAttempts,
      retake_scope: retakeScope ?? (allowRetake ? 'both' : 'none'),
      candidate_can_view_results: candidateCanViewResults,
      auto_approve_assessment: autoApproveAssessment,
      review_mode: reviewMode || (autoApproveAssessment ? 'auto_approve' : 'manual'),
      ...(accessPolicy || {}),
      ...(proctoringPolicy || {})
    };

    return apiRequest<{ assignment: any }>(`/interviews/${interviewId}/assign`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  grantRetake: async (
    interviewId: string,
    data: {
      candidate_id?: string;
      candidate_ids?: string[];
      retake_scope: 'part_a' | 'part_b' | 'both';
      max_attempts?: number;
      completion_deadline?: string | null;
    }
  ) => {
    return apiRequest<{
      retake_scope: string;
      updated_count: number;
      assignments: any[];
    }>(`/interviews/${interviewId}/grant-retake`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  bulkAssign: async (
    interviewId: string,
    data: {
      candidate_ids?: string[];
      section_ids?: string[];
      scheduled_at?: string | null;
      completion_deadline?: string | null;
      auto_approve_assessment?: boolean;
      review_mode?: 'manual' | 'auto_approve' | 'auto_review' | string;
      allow_retake?: boolean;
      max_attempts?: number;
      retake_scope?: 'none' | 'part_a' | 'part_b' | 'both';
      candidate_can_view_results?: boolean;
      restriction_enabled?: boolean;
      allowed_ip_ranges?: string[];
      allowed_locations?: Array<{
        raw_input: string;
        city?: string | null;
        region?: string | null;
        country?: string | null;
        normalized_tokens?: string[];
      }>;
      match_mode?: 'any' | 'all';
      enforcement_mode?: 'start_only' | 'continuous';
      fail_action?: 'block' | 'warn';
      proctoring_enabled?: boolean;
      proctoring_config?: Record<string, any>;
    }
  ) => {
    return apiRequest<{
      outcomes: Array<{
        candidate_id: string;
        full_name?: string;
        outcome: 'created' | 'updated' | 'skipped_not_in_tenant';
      }>;
      summary: {
        created: number;
        updated: number;
        skipped_not_in_tenant: number;
        total_targets: number;
      };
    }>(`/interviews/${interviewId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  listAssignments: async (interviewId: string) => {
    return apiRequest<{
      interview: { id: string; title: string; candidate_count?: number };
      assignments: Array<{
        id: string;
        candidate_id: string;
        interview_id: string;
        status: string;
        scheduled_at?: string | null;
        completion_deadline?: string | null;
        attempt_count?: number;
        max_attempts?: number;
        interview_type?: string;
        candidate_can_view_results?: boolean;
        created_at?: string;
        updated_at?: string;
        full_name: string;
        email: string;
        neptun_code?: string | null;
      }>;
      total: number;
    }>(`/interviews/${interviewId}/assignments`);
  },

  updateResultVisibility: async (
    interviewId: string,
    data: { candidate_can_view_results: boolean }
  ) => {
    return apiRequest<{
      interview_id: string;
      candidate_can_view_results: boolean;
      updated_count: number;
      assignments: Array<{
        id: string;
        candidate_id: string;
        interview_id: string;
        status: string;
        candidate_can_view_results: boolean;
        updated_at: string;
      }>;
    }>(`/interviews/${interviewId}/result-visibility`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },
};

// =============================================================================
// INTERVIEW MATERIALS API
// =============================================================================
export interface InterviewMaterial {
  id: string;
  interview_id: string;
  file_name: string;
  original_name: string;
  file_type: 'pdf' | 'docx' | 'txt' | 'md' | 'image';
  mime_type: string;
  file_size: number;
  description?: string;
  has_extracted_text: boolean;
  uploaded_by?: string;
  created_at: string;
  section_id?: string | null;
  section_name?: string | null;
  category?: string | null;
  linked_at?: string;
}

export interface MaterialsTextResponse {
  definition_version?: number|null;
  session_id?:string;
  materials_count: number;
  combined_text: string;
  materials: { id: string; name: string; type: string; text_length: number }[];
}

export interface MaterialSection {
  id: string;
  name: string;
  description?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  material_count?: number;
}

export interface LibraryMaterial {
  id: string;
  section_id?: string | null;
  section_name?: string | null;
  category?: string | null;
  file_name: string;
  original_name: string;
  file_type: 'pdf' | 'docx' | 'txt' | 'md' | 'image';
  mime_type: string;
  file_size: number;
  description?: string;
  has_extracted_text: boolean;
  linked_interview_count?: number;
  uploaded_by?: string;
  created_at: string;
}

export interface LibraryMaterialSummary {
  total: number;
  with_extracted_text: number;
  categories: string[];
}

export interface LibraryMaterialUsage {
  count: number;
  interviews: { id: string; title: string }[];
  remaining: number;
}

export interface LibraryMaterialExtractedText {
  original_name: string;
  has_extracted_text: boolean;
  char_count: number;
  truncated: boolean;
  text: string;
}

export interface LibraryMaterialMetadataUpdate {
  section_id?: string | null;
  category?: string | null;
  description?: string | null;
  original_name?: string;
}

export const materialSectionsApi = {
  list: async () => {
    return apiRequest<{ sections: MaterialSection[] }>('/material-sections');
  },

  create: async (data: { name: string; description?: string }) => {
    return apiRequest<{ section: MaterialSection }>('/material-sections', {
      method: 'POST',
      suppressGlobalErrorRedirect: true,
      body: JSON.stringify(data)
    });
  },

  update: async (id: string, data: { name?: string; description?: string }) => {
    return apiRequest<{ section: MaterialSection }>(`/material-sections/${id}`, {
      method: 'PUT',
      suppressGlobalErrorRedirect: true,
      body: JSON.stringify(data)
    });
  },

  remove: async (id: string) => {
    return apiRequest<{ message: string }>(`/material-sections/${id}`, {
      method: 'DELETE',
      suppressGlobalErrorRedirect: true
    });
  }
};

export interface MaterialReadinessResult {
  run_id: string;
  agent_id: 'material-readiness-preview';
  definition_version: '1.0.0';
  manifest_hash: string;
  status: 'completed';
  output_schema_version: '1';
  evidence_refs: string[];
  output: { extraction_state: 'available' | 'empty' | 'truncated'; characters: number; revision: string; sharing: 'legacy_shared' | 'tenant_private' };
  warnings: string[];
  requires_review: true;
  error: null;
  trace: { state: string }[];
}
export interface MaterialPreviewRun {
  run_id: string;
  started_at: string;
  status: 'completed' | 'failed' | 'cancelled' | 'timed_out' | 'unresolved';
  revision: string | null;
}

export const materialLibraryApi = {
  previewHistory: async (materialId: string, options: { signal?: AbortSignal } = {}) => {
    return apiRequest<{ runs: MaterialPreviewRun[] }>(`/staff-previews/history/${encodeURIComponent(materialId)}`, {
      signal: options.signal, suppressGlobalErrorRedirect: true,
    });
  },
  previewReadiness: async (materialId: string, options: { signal?: AbortSignal } = {}) => {
    return apiRequest<MaterialReadinessResult>('/staff-previews', {
      method: 'POST', signal: options.signal, suppressGlobalErrorRedirect: true,
      body: JSON.stringify({ agent_id: 'material-readiness-preview', definition_version: '1.0.0', input: { material_id: materialId } }),
    });
  },

  list: async (params?: { section_id?: string; category?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.section_id) query.set('section_id', params.section_id);
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return apiRequest<{ materials: LibraryMaterial[] }>(qs ? `/materials?${qs}` : '/materials');
  },

  summary: async () => {
    return apiRequest<LibraryMaterialSummary>('/materials/summary');
  },

  upload: async (files: File[], data?: { section_id?: string; category?: string; description?: string }) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (data?.section_id) formData.append('section_id', data.section_id);
    if (data?.category) formData.append('category', data.category);
    if (data?.description) formData.append('description', data.description);
    return apiUpload<{ materials: LibraryMaterial[] }>('/materials', formData, { suppressGlobalErrorRedirect: true });
  },

  update: async (materialId: string, data: LibraryMaterialMetadataUpdate) => {
    return apiRequest<{ material: LibraryMaterial }>(`/materials/${materialId}`, {
      method: 'PATCH',
      suppressGlobalErrorRedirect: true,
      body: JSON.stringify(data)
    });
  },

  bulkUpdate: async (ids: string[], data: { section_id?: string | null; category?: string | null }) => {
    return apiRequest<{ updated_count: number; ids: string[] }>('/materials/bulk', {
      method: 'PATCH',
      suppressGlobalErrorRedirect: true,
      body: JSON.stringify({ ids, ...data })
    });
  },

  bulkRemove: async (ids: string[]) => {
    return apiRequest<{ deleted_count: number; deleted: string[]; failed: { id: string; error: string; code?: string }[] }>(
      '/materials/bulk-delete',
      {
        method: 'POST',
        suppressGlobalErrorRedirect: true,
        body: JSON.stringify({ ids })
      }
    );
  },

  usage: async (materialId: string) => {
    return apiRequest<LibraryMaterialUsage>(`/materials/${materialId}/usage`);
  },

  getExtractedText: async (materialId: string) => {
    return apiRequest<LibraryMaterialExtractedText>(`/materials/${materialId}/extracted-text`);
  },

  reextract: async (materialId: string) => {
    return apiRequest<LibraryMaterialExtractedText>(`/materials/${materialId}/reextract`, {
      method: 'POST',
      suppressGlobalErrorRedirect: true
    });
  },

  remove: async (materialId: string) => {
    return apiRequest<{ message: string }>(`/materials/${materialId}`, { method: 'DELETE', suppressGlobalErrorRedirect: true });
  },

  getDownloadUrl: (materialId: string) => {
    return `${API_URL}/api/materials/${materialId}`;
  }
};

export const materialsApi = {
  list: async (interviewId: string) => {
    return apiRequest<{ materials: InterviewMaterial[] }>(`/interviews/${interviewId}/materials`);
  },

  upload: async (
    interviewId: string,
    files: File[],
    data?: { description?: string; section_id?: string; category?: string }
  ) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (data?.description) formData.append('description', data.description);
    if (data?.section_id) formData.append('section_id', data.section_id);
    if (data?.category) formData.append('category', data.category);
    return apiUpload<{ materials: InterviewMaterial[] }>(`/interviews/${interviewId}/materials`, formData, { suppressGlobalErrorRedirect: true });
  },

  link: async (interviewId: string, materialIds: string[]) => {
    return apiRequest<{ added_count: number }>(`/interviews/${interviewId}/materials/link`, {
      suppressGlobalErrorRedirect: true,
      method: 'POST',
      body: JSON.stringify({ material_ids: materialIds })
    });
  },

  getExtractedText: async (interviewId: string, sessionId?: string) => {
    return apiRequest<MaterialsTextResponse>(`/interviews/${interviewId}/materials-text${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`);
  },

  delete: async (interviewId: string, materialId: string) => {
    return apiRequest<{ message: string }>(`/interviews/${interviewId}/materials/${materialId}`, {
      suppressGlobalErrorRedirect: true,
      method: 'DELETE',
    });
  },

  getDownloadUrl: (interviewId: string, materialId: string) => {
    return `${API_URL}/api/interviews/${interviewId}/materials/${materialId}`;
  },
};

// =============================================================================
// MCQ API
// =============================================================================
export interface McqSettings {
  /** Part A (timed MCQ). Alias: part_a_enabled. */
  enabled: boolean;
  /** Explicit Part A flag (mirrors enabled). */
  part_a_enabled?: boolean;
  /** Part B (AI oral viva). Default true. */
  part_b_enabled?: boolean;
  instructions?: string;
  question_count: number;
  difficulty: 'easy' | 'medium' | 'hard' | 'adaptive';
  option_count: number;
  active_set_id?: string | null;
  total_time_seconds?: number | null;
  time_per_question_seconds?: number | null;
  randomize_questions: boolean;
  randomize_options: boolean;
  negative_marking: boolean;
  negative_mark_ratio: number;
  section_config?: Array<{ name: string; count: number }>;
  allow_review: boolean;
  ai_feedback_enabled: boolean;
  require_explanations: boolean;
  question_style: 'direct' | 'scenario' | 'application' | 'mixed';
  strict_materials: boolean;
  avoid_trick_questions: boolean;
  // Ask the text model to describe figures and have the configured image model
  // draw them during generation.
  include_figures?: boolean;
  resource_links?: string[];
  // Combined MCQ (Part A) + oral (Part B) assessment: Part A point scale,
  // topic areas (from the 8 predefined placement topics) to draw reference
  // exemplars from, and whether to ground generation in the uploaded Moodle
  // XML reference question bank at all.
  total_points?: number;
  topic_areas?: number[];
  use_reference_bank?: boolean;
  // 'reference_bank' serves real past placement questions (with their imported
  // figures) instead of writing new ones with the model.
  question_source?: McqQuestionSource;
  // Build a larger pool once and hand every candidate a different slice of it,
  // and give each candidate their own Part B variants.
  dynamic_questions?: boolean;
}

export type McqQuestionSource = 'ai' | 'reference_bank';

export type McqGenerationSource = 'both' | 'interview_only' | 'materials_only';

export type McqGenerationMode = 'manual' | 'ai' | 'upload' | 'reference_bank';
export type McqSourceType = 'prompt' | 'file' | 'reference_bank';
export type McqSetExportFormat = 'json' | 'csv' | 'xml' | 'zip-json' | 'zip-csv' | 'zip-xml';

export interface McqFigureMetadata {
  url: string;
  alt_text: string;
  caption?: string | null;
  width?: number | null;
  height?: number | null;
  suggested?: boolean;
  prompt?: string | null;
}

export type FigureMetadata = McqFigureMetadata;

export type McqContentBlock =
  | { type: 'text'; text: string }
  | { type: 'formula'; latex: string }
  | { type: 'image'; image: McqFigureMetadata };

export interface McqSetSummary {
  id: string;
  status: 'draft' | 'ready' | 'failed';
  created_at?: string;
  updated_at?: string;
  generation_mode?: McqGenerationMode | null;
  source_type?: McqSourceType | null;
  source_reference?: string | null;
  generation_prompt_summary?: string | null;
  generation_timestamp?: string | null;
  generation_source?: McqGenerationSource | null;
  llm_provider?: string | null;
  llm_model?: string | null;
  generated_by?: string | null;
  generated_by_name?: string | null;
  error_message?: string | null;
  question_count: number;
  is_active?: boolean;
}

export interface McqSetQuestion {
  id: string;
  question_text: string;
  question_content?: McqContentBlock[];
  options: string[];
  option_content?: McqContentBlock[][];
  correct_index: number;
  explanation?: string | null;
  explanation_content?: McqContentBlock[];
  figure_metadata?: McqFigureMetadata[];
  generation_mode?: McqGenerationMode | null;
  source_type?: McqSourceType | null;
  source_reference?: string | null;
  generation_model?: string | null;
  generation_prompt_summary?: string | null;
  generation_timestamp?: string | null;
  source_snippets?: string[];
  difficulty?: string | null;
  topic?: string | null;
  section_label?: string | null;
  order_index?: number;
}

export interface McqQuestion {
  id: string;
  answer_revision?: number;
  question_id?: string;
  question: string;
  question_content?: McqContentBlock[];
  options: string[];
  option_content?: McqContentBlock[][];
  explanation?: string | null;
  explanation_content?: McqContentBlock[];
  figure_metadata?: McqFigureMetadata[];
  difficulty?: string;
  topic?: string;
  section?: string | null;
}

export interface McqAttempt {
  id: string;
  session_id?: string;
  interview_id: string;
  status: 'in_progress' | 'submitted' | 'expired';
  started_at?: string;
  submitted_at?: string;
  total_questions?: number;
  correct_count?: number;
  score_percent?: number;
  // Part A (MCQ) score expressed in points (not just percent), combined
  // with Part B's oral score for the overall placement ranking score.
  awarded_points?: number;
  max_points?: number;
}

export interface McqAttemptResponse {
  attempt: McqAttempt;
  settings: McqSettings;
  questions: McqQuestion[];
  responses: Array<{ attempt_item_id: string; selected_index: number | null; flagged?: boolean }>;
}

export interface McqResultQuestion {
  order_index: number;
  section_label?: string | null;
  options: string[];
  option_content?: McqContentBlock[][];
  correct_index: number;
  question_text: string;
  question_content?: McqContentBlock[];
  topic?: string | null;
  difficulty?: string | null;
  explanation?: string | null;
  explanation_content?: McqContentBlock[];
  figure_metadata?: McqFigureMetadata[];
  selected_index?: number | null;
  is_correct?: boolean | null;
  flagged?: boolean | null;
  answered_at?: string | null;
}

export interface McqAttemptResults {
  attempt: McqAttempt & {
    ai_feedback?: {
      strengths?: string[];
      weaknesses?: string[];
      topic_gaps?: string[];
      summary?: string;
    } | null;
  };
  questions: McqResultQuestion[];
}

export interface McqStatus {
  enabled: boolean;
  has_ready_set: boolean;
  ready_set_id?: string | null;
  last_ready_at?: string | null;
  question_count?: number;
}

export interface McqJobRecord {
  id: string;
  job_type: string;
  status: 'queued' | 'running' | 'retry' | 'succeeded' | 'failed' | 'timed_out' | 'cancelled';
  attempts: number;
  max_attempts: number;
  error_message?: string | null;
  result?: any;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  run_after?: string | null;
}

export interface McqGenerateResponse {
  set_id?: string;
  status: string;
  question_count?: number;
  reused?: boolean;
  warnings?: string[];
  queued?: boolean;
  job_id?: string;
  poll_url?: string;
  error?: string;
}

export interface McqAssetUploadResponse {
  asset: {
    file_name: string;
    mime_type: string;
    file_size: number;
    url: string;
  };
}

export interface McqAuthoringSetResponse {
  set: {
    set_id: string;
    status: 'draft' | 'ready' | 'failed';
    active_set_id?: string | null;
    question_count: number;
    generation_mode?: McqGenerationMode;
    source_type?: McqSourceType;
    source_reference?: string | null;
    generation_model?: string | null;
    generation_prompt_summary?: string | null;
    generation_timestamp?: string | null;
  };
  questions?: McqSetQuestion[];
  source?: {
    file_name: string;
    mime_type: string;
    file_size: number;
    snippets: string[];
  };
  provenance?: {
    model: string;
    prompt_summary: string;
    generated_at: string;
    grounding?: {
      kind: 'linked_materials' | 'provided_context' | 'authoring_brief';
      source_count: number;
      truncated: boolean;
      verified_quote_questions: number;
      requires_source_review: number;
      educator_review_required: true;
    };
  };
}

export interface McqImportSummary {
  source_format?: 'xml' | 'json' | 'csv' | string | null;
  imported: number;
  skipped_unsupported: number;
  skipped_declaration?: number;
  skipped_category_nodes?: number;
  skipped_unknown_type?: number;
  truncated?: number;
  errors: Array<{ name?: string | null; qtype?: string; error: string }>;
}

export interface McqImportSetResponse extends McqAuthoringSetResponse {
  summary: McqImportSummary;
  replaced?: boolean;
}

export interface McqAiDraftRequest {
  topic: string;
  domain?: string;
  difficulty?: 'easy' | 'medium' | 'hard' | 'adaptive' | string;
  question_count?: number;
  language?: string;
  target_level?: string;
  question_style?: 'direct' | 'scenario' | 'application' | 'mixed' | string;
  include_figures?: boolean;
  include_formulas?: boolean;
  include_explanations?: boolean;
  include_material_context?: boolean;
  source_context?: string;
  instructions?: string;
  source_reference?: string;
}

export const mcqApi = {
  getSettings: async (interviewId: string) => {
    return apiRequest<{ settings: McqSettings }>(`/mcq/interviews/${interviewId}/settings`);
  },

  getStatus: async (interviewId: string) => {
    return apiRequest<{ status: McqStatus }>(`/mcq/interviews/${interviewId}/status`);
  },

  updateSettings: async (interviewId: string, settings: Partial<McqSettings>) => {
    return apiRequest<{ settings: McqSettings }>(`/mcq/interviews/${interviewId}/settings`, {
      suppressGlobalErrorRedirect: true,
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  },

  resetSettings: async (interviewId: string) => {
    return apiRequest<{ settings: McqSettings; message?: string }>(
      `/mcq/interviews/${interviewId}/settings/reset`,
      {
        suppressGlobalErrorRedirect: true,
        method: 'POST',
        body: JSON.stringify({})
      }
    );
  },

  generate: async (
    interviewId: string,
    options?: {
      force?: boolean;
      queued?: boolean;
      wait?: boolean;
      wait_timeout_ms?: number;
      generation_source?: McqGenerationSource;
    }
  ) => {
    return apiRequest<McqGenerateResponse>(
      `/mcq/interviews/${interviewId}/generate`,
      {
        method: 'POST',
        body: JSON.stringify({
          force: Boolean(options?.force),
          queued: options?.queued,
          wait: options?.wait,
          wait_timeout_ms: options?.wait_timeout_ms,
          generation_source: options?.generation_source || 'both'
        })
      }
    );
  },

  getJob: async (jobId: string) => {
    return apiRequest<{ job: McqJobRecord }>(`/mcq/jobs/${jobId}`);
  },

  waitForJob: async (jobId: string, timeoutMs: number = 20000) => {
    const query = `?timeout_ms=${encodeURIComponent(String(timeoutMs))}`;
    return apiRequest<{ job: McqJobRecord }>(`/mcq/jobs/${jobId}/wait${query}`);
  },

  listSets: async (interviewId: string) => {
    return apiRequest<{ active_set_id: string | null; sets: McqSetSummary[] }>(
      `/mcq/interviews/${interviewId}/sets`
    );
  },

  getSetQuestions: async (setId: string) => {
    return apiRequest<{ questions: McqSetQuestion[] }>(`/mcq/sets/${setId}/questions`);
  },

  exportSet: async (setId: string, format: McqSetExportFormat = 'json') => {
    return apiRequestBlob(`/mcq/sets/${setId}/export?format=${encodeURIComponent(format)}`);
  },

  importSet: async (
    interviewId: string,
    file: File,
    options?: { replaceSetId?: string | null; replaceConfirmed?: boolean }
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.replaceSetId) {
      formData.append('replace_set_id', options.replaceSetId);
    }
    if (options?.replaceConfirmed) {
      formData.append('replace_confirmed', 'true');
    }
    return apiUpload<McqImportSetResponse>(`/mcq/interviews/${interviewId}/authoring/import`, formData, { suppressGlobalErrorRedirect: true });
  },

  uploadAsset: async (interviewId: string, file: File) => {
    const formData = new FormData();
    formData.append('asset', file);
    return apiUpload<McqAssetUploadResponse>(`/mcq/interviews/${interviewId}/assets`, formData);
  },

  createManualDraft: async (
    interviewId: string,
    payload: {
      questions: McqSetQuestion[];
      publish?: boolean;
      make_active?: boolean;
      source_reference?: string;
      title?: string;
    }
  ) => {
    return apiRequest<McqAuthoringSetResponse>(`/mcq/interviews/${interviewId}/authoring/manual-draft`, {
      suppressGlobalErrorRedirect: true,
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  generateDraftByAi: async (interviewId: string, payload: McqAiDraftRequest) => {
    return apiRequest<McqAuthoringSetResponse>(`/mcq/interviews/${interviewId}/authoring/ai-draft`, {
      suppressGlobalErrorRedirect: true,
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  generateDraftFromUpload: async (
    interviewId: string,
    file: File,
    payload: {
      topic?: string;
      domain?: string;
      difficulty?: string;
      question_count?: number;
      language?: string;
      target_level?: string;
      question_style?: string;
      include_figures?: boolean;
      include_formulas?: boolean;
      include_explanations?: boolean;
      instructions?: string;
      source_reference?: string;
    }
  ) => {
    const formData = new FormData();
    formData.append('file', file);

    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      formData.append(key, String(value));
    });

    return apiUpload<McqAuthoringSetResponse>(`/mcq/interviews/${interviewId}/authoring/upload-draft`, formData, { suppressGlobalErrorRedirect: true });
  },

  updateSetQuestions: async (
    setId: string,
    payload: {
      questions: McqSetQuestion[];
      publish?: boolean;
      make_active?: boolean;
      generation_mode?: McqGenerationMode;
      source_type?: McqSourceType;
      source_reference?: string;
      generation_model?: string;
      generation_prompt_summary?: string;
      generation_timestamp?: string;
    }
  ) => {
    return apiRequest<McqAuthoringSetResponse>(`/mcq/sets/${setId}/questions`, {
      suppressGlobalErrorRedirect: true,
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  },

  publishSet: async (setId: string, makeActive: boolean = true) => {
    return apiRequest<McqAuthoringSetResponse>(`/mcq/sets/${setId}/publish`, {
      suppressGlobalErrorRedirect: true,
      method: 'POST',
      body: JSON.stringify({ make_active: makeActive })
    });
  },

  regenerateQuestion: async (
    setId: string,
    questionId: string,
    payload?: {
      topic?: string;
      difficulty?: string;
      question_style?: string;
      include_figures?: boolean;
      include_formulas?: boolean;
      include_explanations?: boolean;
      instructions?: string;
      source_context?: string;
    }
  ) => {
    return apiRequest<{ question: McqSetQuestion; provenance?: { model: string; prompt_summary: string; generated_at: string } }>(
      `/mcq/sets/${setId}/questions/${questionId}/regenerate`,
      {
        suppressGlobalErrorRedirect: true,
        method: 'POST',
        body: JSON.stringify(payload || {})
      }
    );
  },

  regenerateFigures: async (setId: string) => {
    return apiRequest<{
      generated: number;
      failed: number;
      synthesizedPrompts: number;
      questions: McqSetQuestion[];
    }>(`/mcq/sets/${setId}/regenerate-figures`, {
      suppressGlobalErrorRedirect: true,
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  setActiveSet: async (interviewId: string, setId: string | null) => {
    return apiRequest<{ active_set_id: string | null }>(`/mcq/interviews/${interviewId}/active-set`, {
      suppressGlobalErrorRedirect: true,
      method: 'PUT',
      body: JSON.stringify({ set_id: setId })
    });
  },

  startAttempt: async (interviewId: string, sessionId?: string) => {
    const query = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
    return apiRequest<McqAttemptResponse>(`/mcq/interviews/${interviewId}/attempt${query}`);
  },

  saveAnswer: async (attemptId: string, data: {
    attempt_item_id: string;
    selected_index: number | null;
    time_taken_seconds?: number;
    flagged?: boolean;
    expected_revision?: number;
    request_id?: string;
  }) => {
    return apiRequest<{ ok: boolean; is_correct: boolean | null; answer_revision?:number; replayed?:boolean }>(`/mcq/attempts/${attemptId}/answer`, {
      method: 'POST',
      body: JSON.stringify(data),
        suppressGlobalErrorRedirect: true
    });
  },

  submitAttempt: async (attemptId: string, answerRevisions?:Record<string,number>) => {
    return apiRequest<{
      status: McqAttempt['status'];
      score_percent: number;
      correct: number;
      total: number;
      awarded_points?: number;
      max_points?: number;
      assessment_stage?: 'part_a' | 'part_b' | 'completed' | string | null;
      part_b?: {
        triggered?: boolean;
        skipped?: boolean;
        completion_pending?: boolean;
        error?: string | null;
        generate_status?: string | null;
        generate_job_id?: string | null;
        finalized?: {
          assessment_id?: string | null;
          placement_written?: boolean;
        };
      };
      feedback_job_id?: string | null;
      feedback_status?: 'queued' | 'unavailable' | 'disabled';
    }>(
      `/mcq/attempts/${attemptId}/submit`,
        { method: 'POST', body:answerRevisions ? JSON.stringify({answer_revisions:answerRevisions}) : undefined, suppressGlobalErrorRedirect: true }
    );
  },

  getResults: async (attemptId: string) => {
    return apiRequest<McqAttemptResults>(`/mcq/attempts/${attemptId}/results`);
  }
};

// =============================================================================
// SESSIONS API
// =============================================================================
export interface InterviewSession {
  id: string;
  candidate_id: string;
  interview_id: string;
  timing_snapshot?: {version:number;part_b_duration_seconds:number}|null;
  context_snapshot?:SessionContextDefinition|null;
  retake_snapshot?:SessionRetakeDefinition|null;
  mode_snapshot?:SessionModeDefinition|null;
  material_definition_version?:number|null;
  prompt_definition_version?:number|null;
  dialogue_definition_version?:number|null;
  response_definition_version?:number|null;
  status: string;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  writing_response?: string;
  transcript?: any[];
  integrity_events?: any[];
  integrity_score?: number;
  // Combined MCQ (Part A) + oral (Part B) assessment: which part of the
  // sequential flow this session is currently in.
  assessment_stage?: 'part_a' | 'part_b' | 'completed';
}

export interface SessionAutosavePayload {
  model_recovery?:{version:1;request_id:string;base_revision:number};
  expected_revision?: number;
  request_id?: string;
  phase?: string;
  step?: number;
  payload?: Record<string, any>;
  transcript_length?: number;
  suspicion_score?: number;
  network_state?: 'online' | 'offline';
}

export interface SessionResumeState {
  transcript_reconciliation_version?:1;
  session: {
    transcript?:unknown[];
    id: string;
    status: string;
    assessment_stage?: 'part_a' | 'part_b' | 'completed';
    connection_status: 'connected' | 'reconnecting' | 'disconnected';
    current_phase?: string | null;
    current_step?: number | null;
    last_heartbeat_at?: string | null;
    dropped_at?: string | null;
    resumed_at?: string | null;
    remaining_seconds?: number | null;
    active_seconds_consumed?: number | null;
    timer_paused_at?: string | null;
    pause_reason?: string | null;
  };
  autosave: {
    revision?: number;
    phase?: string | null;
    step?: number | null;
    payload?: Record<string, any>;
    transcript_length?: number;
    suspicion_score?: number;
    network_state?: 'online' | 'offline';
    updated_at?: string | null;
  };
  history: Array<{
    phase?: string | null;
    step?: number | null;
    network_state?: 'online' | 'offline';
    created_at: string;
  }>;
  /** Whether the candidate may reconnect and continue (deadline/window gate). */
  resumable?: boolean;
  blocked_reason?: string | null;
  blocked_status?: number | null;
  blocked_message?: string | null;
  blocked_details?: Record<string, unknown> | null;
}

export interface SessionAccessCheckResult {
  allowed: boolean;
  decision: 'pass' | 'warn' | 'block';
  code: 'LOCATION_OR_IP_NOT_ALLOWED' | null;
  message: string;
  details: {
    current_ip?: string | null;
    detected_location?: {
      city?: string | null;
      region?: string | null;
      country?: string | null;
      country_code?: string | null;
    } | null;
    required_policy_summary?: Record<string, any> | null;
  };
}

export interface SessionProctoringPolicy {
  enabled: boolean;
  require_consent: boolean;
  evidence_mode: 'metadata_only' | 'extended';
  source: 'interview' | 'assignment';
  config: Record<string, any>;
}

export interface CandidateProctoringWarning {
  level: 'notice' | 'strong';
  title: string;
  message: string;
  anomaly_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  recent_medium_count: number;
  countdown_seconds: number;
  risk_score: number;
  recommendation: 'clear' | 'review' | 'high_risk';
}

export interface SessionProctoringStatus {
  session_id: string;
  feature_enabled: boolean;
  policy: SessionProctoringPolicy;
  consent: {
    required: boolean;
    given: boolean;
    given_at?: string | null;
  };
  summary: {
    integrity_score: number;
    risk_score: number;
    recommendation: 'clear' | 'review' | 'high_risk';
    total_events: number;
    events_by_severity: {
      low: number;
      medium: number;
      high: number;
      critical: number;
    };
    open_alerts: number;
    resolved_alerts: number;
    total_alerts: number;
    last_event_at?: string | null;
  };
}

export interface SessionProctoringEventInput {
  anomaly_type: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  source?: 'camera' | 'audio' | 'system';
  confidence?: number;
  occurred_at?: string;
  client_detected_at?: string;
  evidence_ref?: string;
  evidence_url?: string;
  metadata?: Record<string, any>;
}

export interface SessionProctoringEvidenceMeta {
  id: string;
  session_id: string;
  event_id?: string | null;
  anomaly_type?: string | null;
  mime_type?: string;
  byte_size?: number;
  width?: number | null;
  height?: number | null;
  retention_days?: number;
}

export interface SessionProctoringReport {
  session_id: string;
  candidate_id: string;
  interview_id: string;
  interview_title?: string;
  feature_enabled: boolean;
  policy: SessionProctoringPolicy;
  consent: {
    required: boolean;
    given: boolean;
    given_at?: string | null;
  };
  summary: {
    integrity_score: number;
    risk_score: number;
    recommendation: 'clear' | 'review' | 'high_risk';
    total_events: number;
    events_by_severity: {
      low: number;
      medium: number;
      high: number;
      critical: number;
    };
    alerts_count: number;
    last_event_at?: string | null;
  };
  events: Array<{
    id: string;
    anomaly_type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    confidence: number;
    source: 'camera' | 'audio' | 'system';
    metadata?: Record<string, any>;
    evidence_mode: 'metadata_only' | 'extended';
    evidence_ref?: string | null;
    review_status?: 'unreviewed' | 'reviewed' | 'false_positive';
    reviewed_by?: string | null;
    reviewed_at?: string | null;
    review_note?: string | null;
    exclude_from_score?: boolean;
    client_detected_at?: string | null;
    occurred_at: string;
    created_at: string;
    created_by?: string | null;
  }>;
  alerts: InvigilatorAlert[];
}

export interface InvigilatorMessage {
  id: string;
  session_id: string;
  from_user_id: string;
  to_user_id: string;
  message: string;
  created_at: string;
  read_at?: string;
  sender_name?: string;
  sender_role?: string;
}

export interface SessionCompletionStatus {
  accepted: boolean;
  assessment: 'available' | 'missing' | 'ambiguous' | 'not_submitted';
  scoring: 'not_submitted' | 'unknown' | 'queued' | 'running' | 'retrying' | 'processed' | 'needs_attention';
}

export interface CompletionReconciliationItem {
  session_id: string;
  candidate_id: string;
  interview_id: string;
  created_at: string;
  completion: SessionCompletionStatus;
}

export interface CompletionRepairPreview {
  eligible: true;
  component: 'part_a' | 'oral';
  attempts_remaining: number;
  expires_at: string;
  preview_token: string;
}

export interface WritingTaskDefinition {id:string;title:string;prompt:string;max_words:number;rubric?:string}
export interface WritingResponse {task_id:string;revision:number;content:string;state:'draft'|'submitted'}
export interface SessionWritingState {version:1;adopted:boolean;editable:boolean;source_revision:number|null;tasks:WritingTaskDefinition[];responses:WritingResponse[]}
export interface WritingResponseWrite {request_id:string;expected_revision:number;content:string;state:'draft'|'submitted'}
export interface WritingResponseAck {ok:true;task_id:string;request_id:string;revision:number;state:'draft'|'submitted'}
export const writingApi={
 readTasks:(id:string)=>apiRequest<{revision:number;tasks:WritingTaskDefinition[]}>(`/writing/interviews/${id}`),
 replaceTasks:(id:string,expected_revision:number,tasks:WritingTaskDefinition[])=>apiRequest<{revision:number;tasks:WritingTaskDefinition[]}>(`/writing/interviews/${id}`,{method:'PUT',body:JSON.stringify({expected_revision,tasks})}),
 readSession:(id:string)=>apiRequest<SessionWritingState>(`/writing/sessions/${id}`),
 save:(id:string,taskId:string,body:WritingResponseWrite)=>apiRequest<WritingResponseAck>(`/writing/sessions/${id}/tasks/${taskId}`,{method:'PUT',body:JSON.stringify(body),signal:AbortSignal.timeout(15000)})
};

export const sessionsApi = {
  previewCompletionRepair: async (id: string) => apiRequest<CompletionRepairPreview>(
    `/sessions/${id}/completion-repair`, { suppressGlobalErrorRedirect: true }
  ),
  executeCompletionRepair: async (id: string, previewToken: string, requestId: string) => apiRequest<{accepted: true;receipt_id: string;replayed: boolean}>(
    `/sessions/${id}/completion-repair`, { method: 'POST', body: JSON.stringify({preview_token: previewToken, request_id: requestId, reason: 'transient_failure_resolved'}), suppressGlobalErrorRedirect: true }
  ),
  reconciliation: async (before?: string) => apiRequest<{ items: CompletionReconciliationItem[]; next_cursor: string | null }>(
    `/sessions/completion-reconciliation?limit=25${before ? `&before=${encodeURIComponent(before)}` : ''}`,
    { suppressGlobalErrorRedirect: true }
  ),
  completionStatus: async (id: string, options: {signal?:AbortSignal} = {}) => apiRequest<{ completion: SessionCompletionStatus }>(
    `/sessions/${id}/completion-status`, { suppressGlobalErrorRedirect: true,signal:options.signal }
  ),
  list: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest<{ sessions: InterviewSession[] }>(`/sessions?${query}`);
  },

  get: async (id: string) => {
    return apiRequest<{ session: InterviewSession;oral_definition?:SessionAuthoredDefinition|null }>(`/sessions/${id}`);
  },

  start: async (interviewId: string, browserInfo?: any, technicalChecks?: any) => {
    return apiRequest<{
      session: InterviewSession;
      oral_definition?:SessionAuthoredDefinition|null;
      resumed?: boolean;
      access_warning?: SessionAccessCheckResult;
      proctoring?: {
        feature_enabled: boolean;
        policy: SessionProctoringPolicy;
        consent: {
          required: boolean;
          given: boolean;
          given_at?: string | null;
        };
      };
    }>('/sessions', {
      method: 'POST',
      body: JSON.stringify({ 
        interview_id: interviewId, 
        browser_info: browserInfo,
        technical_checks: technicalChecks 
      }),
    });
  },

  preflight: async (interviewId: string) => {
    return apiRequest<{
      access: SessionAccessCheckResult;
      proctoring?: {
        feature_enabled: boolean;
        policy: SessionProctoringPolicy;
        consent: {
          required: boolean;
          given: boolean;
          given_at?: string | null;
        };
      };
    }>('/sessions/preflight', {
      method: 'POST',
      body: JSON.stringify({ interview_id: interviewId })
    });
  },

  update: async (id: string, data: Partial<InterviewSession>) => {
    return apiRequest<{ session: InterviewSession }>(`/sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  heartbeat: async (
    id: string,
    data: {
      connection_status?: 'connected' | 'reconnecting' | 'disconnected';
      current_phase?: string;
      current_step?: number;
      network_state?: 'online' | 'offline';
      remaining_seconds?: number | null;
      active_seconds_consumed?: number | null;
      pause_reason?: string;
    }
  ) => {
    return apiRequest<{ heartbeat: any }>(`/sessions/${id}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  autosave: async (id: string, data: SessionAutosavePayload) => {
    return apiRequest<{
      ok: boolean;
      model_applications?:ModelApplicationAcknowledgement;
      autosave: {
        session_id: string;
        revision?: number;
        request_id?: string | null;
        phase?: string | null;
        step?: number;
        network_state?: string;
        updated_at?: string;
      };
    }>(`/sessions/${id}/autosave`, {
      method: 'POST',
      body: JSON.stringify(data),
      suppressGlobalErrorRedirect: true
    });
  },

  getResumeState: async (id: string) => {
    return apiRequest<SessionResumeState>(`/sessions/${id}/resume`);
  },

  reconnect: async (
    id: string,
    reason?: string,
    timer?: { remaining_seconds?: number | null; active_seconds_consumed?: number | null }
  ) => {
    return apiRequest<{ session: InterviewSession }>(`/sessions/${id}/reconnect`, {
      method: 'POST',
      body: JSON.stringify({ reason, ...(timer || {}) })
    });
  },

  getProctoringStatus: async (id: string) => {
    return apiRequest<SessionProctoringStatus>(`/sessions/${id}/proctoring/status`);
  },

  submitProctoringConsent: async (id: string, consent: boolean = true) => {
    return apiRequest<{
      session_id: string;
      policy: SessionProctoringPolicy;
      consent: {
        required: boolean;
        given: boolean;
        given_at?: string | null;
      };
    }>(`/sessions/${id}/proctoring/consent`, {
      method: 'POST',
      body: JSON.stringify({ consent })
    });
  },

  pushProctoringEvents: async (id: string, events: SessionProctoringEventInput[] | SessionProctoringEventInput) => {
    return apiRequest<{
      accepted: boolean;
      processed_count?: number;
      feature_enabled?: boolean;
      reason?: string;
      policy?: SessionProctoringPolicy;
      integrity_score?: number;
      risk_score?: number;
      recommendation?: 'clear' | 'review' | 'high_risk';
      warning?: CandidateProctoringWarning | null;
      alerts_created?: number;
      alerts?: Array<{
        id: string;
        invigilator_id: string;
        title: string;
        severity: 'low' | 'medium' | 'high' | 'critical';
        status: 'new' | 'acknowledged' | 'investigating' | 'dismissed' | 'resolved';
        created_at: string;
      }>;
    }>(`/sessions/${id}/proctoring/events`, {
      method: 'POST',
      body: JSON.stringify(Array.isArray(events) ? { events } : events)
    });
  },

  getProctoringReport: async (
    id: string,
    params?: { severity?: 'low' | 'medium' | 'high' | 'critical'; limit?: number }
  ) => {
    const query = new URLSearchParams();
    if (params?.severity) query.set('severity', params.severity);
    if (params?.limit) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return apiRequest<SessionProctoringReport>(
      `/sessions/${id}/proctoring/report${suffix ? `?${suffix}` : ''}`
    );
  },

  uploadProctoringEvidence: async (
    id: string,
    data: {
      blob: Blob;
      anomalyType?: string;
      eventId?: string;
      width?: number;
      height?: number;
    }
  ) => {
    const formData = new FormData();
    formData.append('screenshot', data.blob, `evidence-${Date.now()}.jpg`);
    if (data.anomalyType) formData.append('anomaly_type', data.anomalyType);
    if (data.eventId) formData.append('event_id', data.eventId);
    if (data.width != null) formData.append('width', String(data.width));
    if (data.height != null) formData.append('height', String(data.height));
    return apiUpload<{
      evidence: SessionProctoringEvidenceMeta;
      evidence_ref: string;
    }>(`/sessions/${id}/proctoring/evidence`, formData);
  },

  getProctoringEvidenceBlob: async (sessionId: string, evidenceId: string) => {
    return apiRequestBlob(`/sessions/${sessionId}/proctoring/evidence/${evidenceId}`);
  },

  reviewProctoringEvent: async (
    sessionId: string,
    eventId: string,
    data: {
      review_status: 'reviewed' | 'false_positive' | 'unreviewed';
      review_note?: string;
    }
  ) => {
    return apiRequest<{
      event: SessionProctoringReport['events'][number];
      integrity_score: number;
      risk_score: number;
      recommendation: 'clear' | 'review' | 'high_risk';
    }>(`/sessions/${sessionId}/proctoring/events/${eventId}/review`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  purgeProctoringEvidence: async (sessionId: string, olderThanDays = 90) => {
    return apiRequest<{ purged: number; older_than_days: number }>(
      `/sessions/${sessionId}/proctoring/evidence/purge?older_than_days=${olderThanDays}`,
      { method: 'DELETE' }
    );
  },

  publishLiveHeartbeat: async (
    sessionId: string,
    data: {
      screenShareActive?: boolean;
      fullscreenActive?: boolean;
      environmentCameraActive?: boolean;
      assessmentStage?: 'part_a' | 'part_b' | 'completed';
    }
  ) => {
    return apiRequest<{ ok: boolean; ttl_ms: number }>(`/sessions/${sessionId}/live/heartbeat`, {
      method: 'POST',
      body: JSON.stringify({
        screen_share_active: Boolean(data.screenShareActive),
        fullscreen_active: Boolean(data.fullscreenActive),
        environment_camera_active: Boolean(data.environmentCameraActive),
        assessment_stage: data.assessmentStage || null
      })
    });
  },

  publishLiveFrames: async (
    sessionId: string,
    data: {
      cameraBlob?: Blob;
      screenBlob?: Blob;
      environmentBlob?: Blob;
      screenShareActive?: boolean;
      fullscreenActive?: boolean;
      assessmentStage?: 'part_a' | 'part_b' | 'completed';
    }
  ) => {
    const formData = new FormData();
    if (data.cameraBlob) formData.append('camera', data.cameraBlob, `camera-${Date.now()}.jpg`);
    if (data.screenBlob) formData.append('screen', data.screenBlob, `screen-${Date.now()}.jpg`);
    if (data.environmentBlob) formData.append('environment', data.environmentBlob, `room-${Date.now()}.jpg`);
    formData.append('screen_share_active', String(Boolean(data.screenShareActive)));
    formData.append('fullscreen_active', String(Boolean(data.fullscreenActive)));
    if (data.assessmentStage) {
      formData.append('assessment_stage', data.assessmentStage);
    }
    return apiUpload<{ ok: boolean; ttl_ms: number }>(`/sessions/${sessionId}/live/frames`, formData);
  },

  getLiveCameraBlob: async (sessionId: string) => {
    return apiRequestBlob(`/sessions/${sessionId}/live/camera`);
  },

  getLiveScreenBlob: async (sessionId: string) => {
    return apiRequestBlob(`/sessions/${sessionId}/live/screen`);
  },

  getLiveRoomBlob: async (sessionId: string) => {
    return apiRequestBlob(`/sessions/${sessionId}/live/room`);
  },

  createRoomCameraPairing: async (sessionId: string) => {
    return apiRequest<{ pairing_url: string; expires_at: string }>(`/sessions/${sessionId}/secondary-camera/pairing`, {
      method: 'POST'
    });
  },

  skipRoomCamera: async (sessionId: string) => {
    return apiRequest<{ ok: boolean; status: string }>(`/sessions/${sessionId}/secondary-camera/skip`, {
      method: 'POST'
    });
  },

  getRoomCameraStatus: async (sessionId: string) => {
    return apiRequest<{
      enabled: boolean;
      required: boolean;
      status: string;
      paired_at?: string | null;
      last_frame_at?: string | null;
      alert?: { type: string; message: string } | null;
    }>(`/sessions/${sessionId}/secondary-camera/status`);
  },

  flagRoomCamera: async (sessionId: string, anomalyType: string) => {
    return apiRequest<{ ok: boolean; anomaly_type: string }>(`/sessions/${sessionId}/secondary-camera/flag`, {
      method: 'POST',
      body: JSON.stringify({ anomaly_type: anomalyType })
    });
  },

  complete: async (id: string, data?: { transcript?: any[]; integrity_events?: any[]; integrity_score?: number }, options: {signal?:AbortSignal} = {}) => {
    return apiRequest<{ session: InterviewSession; assessment_id: string }>(`/sessions/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
      signal:options.signal,
      suppressGlobalErrorRedirect:true,
    });
  },

  listMessages: async (id: string) => {
    return apiRequest<{ messages: InvigilatorMessage[] }>(`/sessions/${id}/messages`);
  },

  sendMessage: async (id: string, message: string) => {
    return apiRequest<{ message: InvigilatorMessage; recipients: number }>(`/sessions/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  },

  retake: async (interviewId: string) => {
    return apiRequest<{ message: string; attempts_used: number; max_attempts: number }>(`/sessions/retake/${interviewId}`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// INVIGILATION API
// =============================================================================
export interface InvigilatorAssignment {
  assignment_id: string;
  assignment_created_at?: string;
  assignment_updated_at?: string;
  invigilator_id?: string;
  invigilator_name?: string;
  invigilator_email?: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  candidate_neptun_code?: string | null;
  interview_id: string;
  interview_title: string;
  interview_scheduled_at?: string | null;
  available_from?: string | null;
  available_until?: string | null;
  candidate_assignment_status?: string | null;
  section_id?: string | null;
  assignment_status: string;
  session_id?: string;
  session_status?: string;
  started_at?: string;
  integrity_score?: number;
  integrity_events?: any[];
  transcript?: any[];
}

export interface ProctoringTimelineEvent {
  id: string;
  session_id: string;
  candidate_id: string;
  event_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: 'system' | 'client' | 'invigilator' | 'admin';
  evidence_snippet?: string | null;
  evidence_url?: string | null;
  metadata?: Record<string, any>;
  occurred_at: string;
  created_by?: string | null;
  created_by_name?: string;
  created_by_role?: string;
}

export interface ProctoringReportSummary {
  session_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  interview_id: string;
  interview_title: string;
  session_status: string;
  started_at?: string | null;
  completed_at?: string | null;
  integrity_score: number;
  risk_score: number;
  recommendation: 'clear' | 'review' | 'high_risk' | string;
  event_count: number;
  unreviewed_count: number;
  evidence_count: number;
  last_proctoring_activity_at?: string | null;
  evidence_mode: 'metadata_only' | 'extended' | string;
  proctoring_enabled?: boolean;
  evidence_retention_days?: number;
  consent_given_at?: string | null;
}

export interface LiveMonitoringSession {
  session_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  interview_id: string;
  interview_title: string;
  session_status: string;
  assessment_stage?: 'part_a' | 'part_b' | 'completed' | string | null;
  section_id?: string | null;
  section_name?: string | null;
  integrity_score: number;
  risk_score: number;
  recommendation: string;
  connection_health?: 'live' | 'stale' | 'none' | string;
  started_at?: string | null;
  last_proctoring_activity_at?: string | null;
  screen_share_started_at?: string | null;
  screen_share_last_seen_at?: string | null;
  fullscreen_locked_at?: string | null;
  live?: {
    updated_at: string;
    has_camera: boolean;
    has_screen: boolean;
    has_room?: boolean;
    screen_share_active: boolean;
    fullscreen_active: boolean;
    assessment_stage?: 'part_a' | 'part_b' | 'completed' | string | null;
    age_ms?: number | null;
  } | null;
  room_camera?: {
    enabled: boolean;
    status: string;
    last_frame_at?: string | null;
    paired_at?: string | null;
  };
}

export interface LiveSessionsListParams {
  interview_id?: string;
  section_id?: string;
  search?: string;
  stage?: 'part_a' | 'part_b' | 'completed' | string;
  feed?: 'camera' | 'screen' | 'both' | 'missing_camera' | 'missing_screen' | 'environment' | 'missing_environment' | 'all' | string;
  connection?: 'live' | 'stale' | 'none' | string;
}

export interface ProctoringReportListParams {
  interview_id?: string;
  search?: string;
  recommendation?: 'clear' | 'review' | 'high_risk';
  status?: string;
  has_unreviewed?: boolean;
  has_evidence?: boolean;
  start_date?: string;
  end_date?: string;
  invigilator_id?: string;
  limit?: number;
  offset?: number;
}

export interface InvigilatorAlert {
  id: string;
  session_id: string;
  event_id?: string | null;
  invigilator_id: string;
  invigilator_name?: string;
  candidate_id: string;
  candidate_name?: string;
  candidate_email?: string;
  interview_id: string;
  interview_title?: string;
  title: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'new' | 'acknowledged' | 'investigating' | 'dismissed' | 'resolved';
  incident_key?: string;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  resolution_notes?: string | null;
  metadata?: Record<string, any>;
  session_status?: string;
  created_at: string;
  updated_at?: string;
}

export const invigilationApi = {
  listAssignments: async () => {
    return apiRequest<{ assignments: InvigilatorAssignment[] }>('/invigilation/assignments');
  },

  listAdminAssignments: async (filters?: {
    invigilator_id?: string;
    interview_id?: string;
    status?: 'active' | 'inactive';
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();

    if (filters?.invigilator_id) params.set('invigilator_id', filters.invigilator_id);
    if (filters?.interview_id) params.set('interview_id', filters.interview_id);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.search) params.set('search', filters.search);
    if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
    if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));

    const query = params.toString();
    return apiRequest<{ assignments: InvigilatorAssignment[] }>(
      `/invigilation/admin-assignments${query ? `?${query}` : ''}`
    );
  },

  getSession: async (sessionId: string) => {
    return apiRequest<{ session: InterviewSession & { candidate_name?: string; candidate_email?: string; interview_title?: string } }>(
      `/invigilation/sessions/${sessionId}`
    );
  },

  listMessages: async (sessionId: string) => {
    return apiRequest<{ messages: InvigilatorMessage[] }>(`/invigilation/sessions/${sessionId}/messages`);
  },

  sendMessage: async (sessionId: string, message: string) => {
    return apiRequest<{ message: InvigilatorMessage }>(`/invigilation/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  },

  listAlerts: async (filters?: {
    status?: 'new' | 'acknowledged' | 'investigating' | 'dismissed' | 'resolved';
    severity?: 'low' | 'medium' | 'high' | 'critical';
    session_id?: string;
    invigilator_id?: string;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.severity) params.set('severity', filters.severity);
    if (filters?.session_id) params.set('session_id', filters.session_id);
    if (filters?.invigilator_id) params.set('invigilator_id', filters.invigilator_id);
    if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
    const query = params.toString();

    return apiRequest<{ alerts: InvigilatorAlert[] }>(
      `/invigilation/alerts${query ? `?${query}` : ''}`
    );
  },

  listSessionAlerts: async (
    sessionId: string,
    filters?: {
      status?: 'new' | 'acknowledged' | 'investigating' | 'dismissed' | 'resolved';
      severity?: 'low' | 'medium' | 'high' | 'critical';
      limit?: number;
    }
  ) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.severity) params.set('severity', filters.severity);
    if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
    const query = params.toString();

    return apiRequest<{ session_id: string; alerts: InvigilatorAlert[] }>(
      `/invigilation/sessions/${sessionId}/alerts${query ? `?${query}` : ''}`
    );
  },

  updateAlertStatus: async (
    alertId: string,
    data: {
      status?: 'new' | 'acknowledged' | 'investigating' | 'dismissed' | 'resolved';
      resolution_notes?: string;
    }
  ) => {
    return apiRequest<{ alert: InvigilatorAlert }>(`/invigilation/alerts/${alertId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  listProctoringReports: async (filters?: ProctoringReportListParams) => {
    const params = new URLSearchParams();
    if (filters?.interview_id) params.set('interview_id', filters.interview_id);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.recommendation) params.set('recommendation', filters.recommendation);
    if (filters?.status) params.set('status', filters.status);
    if (typeof filters?.has_unreviewed === 'boolean') {
      params.set('has_unreviewed', String(filters.has_unreviewed));
    }
    if (typeof filters?.has_evidence === 'boolean') {
      params.set('has_evidence', String(filters.has_evidence));
    }
    if (filters?.start_date) params.set('start_date', filters.start_date);
    if (filters?.end_date) params.set('end_date', filters.end_date);
    if (filters?.invigilator_id) params.set('invigilator_id', filters.invigilator_id);
    if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
    if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
    const query = params.toString();

    return apiRequest<{
      reports: ProctoringReportSummary[];
      pagination: {
        limit: number;
        offset: number;
        total: number;
        has_more: boolean;
      };
    }>(`/invigilation/proctoring-reports${query ? `?${query}` : ''}`);
  },

  getProctoringReportSummary: async (sessionId: string) => {
    return apiRequest<{ report: ProctoringReportSummary }>(
      `/invigilation/proctoring-reports/${sessionId}`
    );
  },

  listTimeline: async (sessionId: string, limit: number = 200) => {
    return apiRequest<{ session_id: string; events: ProctoringTimelineEvent[] }>(
      `/invigilation/sessions/${sessionId}/timeline?limit=${encodeURIComponent(String(limit))}`
    );
  },

  createTimelineEvent: async (
    sessionId: string,
    data: {
      event_type: string;
      severity?: 'low' | 'medium' | 'high' | 'critical';
      source?: 'system' | 'client' | 'invigilator' | 'admin';
      evidence_snippet?: string;
      evidence_url?: string;
      metadata?: Record<string, any>;
      occurred_at?: string;
    }
  ) => {
    return apiRequest<{ event: ProctoringTimelineEvent }>(`/invigilation/sessions/${sessionId}/timeline`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  assign: async (invigilatorId: string, candidateIds: string[], interviewId: string) => {
    return apiRequest<{
      assignments: { id: string; candidate_id: string }[];
      added_count: number;
      candidates: string[];
      skipped_candidates?: string[];
    }>(
      `/invigilation/assignments`,
      {
        method: 'POST',
        body: JSON.stringify({
          invigilator_id: invigilatorId,
          candidate_ids: candidateIds,
          interview_id: interviewId
        })
      }
    );
  },

  assignBySection: async (invigilatorId: string, sectionId: string, interviewId: string) => {
    return apiRequest<{
      assignments: { id: string; candidate_id: string }[];
      added_count: number;
      candidates: string[];
      skipped_candidates?: string[];
      section_id: string;
      section_name?: string;
    }>('/invigilation/assignments/by-section', {
      method: 'POST',
      body: JSON.stringify({
        invigilator_id: invigilatorId,
        section_id: sectionId,
        interview_id: interviewId
      })
    });
  },

  listLiveSessions: async (filters?: LiveSessionsListParams) => {
    const params = new URLSearchParams();
    if (filters?.interview_id) params.set('interview_id', filters.interview_id);
    if (filters?.section_id) params.set('section_id', filters.section_id);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.stage) params.set('stage', filters.stage);
    if (filters?.feed) params.set('feed', filters.feed);
    if (filters?.connection) params.set('connection', filters.connection);
    const query = params.toString();
    return apiRequest<{
      sessions: LiveMonitoringSession[];
      total?: number;
      total_before_live_filters?: number;
      filters?: {
        sections: Array<{ id: string; name: string }>;
        interviews: Array<{ id: string; title: string }>;
      };
    }>(`/invigilation/live-sessions${query ? `?${query}` : ''}`);
  },

  removeAssignment: async (assignmentId: string) => {
    return apiRequest<{
      message: string;
      assignment?: {
        id: string;
        invigilator_id: string;
        candidate_id: string;
        interview_id: string;
        status: string;
      };
    }>(`/invigilation/assignments/${assignmentId}`, {
      method: 'DELETE'
    });
  },

  reassignAssignment: async (assignmentId: string, invigilatorId: string) => {
    return apiRequest<{
      message: string;
      previous_assignment?: {
        id: string;
        invigilator_id: string;
        candidate_id: string;
        interview_id: string;
        status: string;
      };
      reassigned_assignment?: {
        id: string;
        invigilator_id: string;
        candidate_id: string;
        interview_id: string;
        status: string;
      };
    }>(`/invigilation/assignments/${assignmentId}/reassign`, {
      method: 'POST',
      body: JSON.stringify({ invigilator_id: invigilatorId })
    });
  }
};

// =============================================================================
// SECTIONS API
// =============================================================================
export interface CandidateSection {
  id: string;
  name: string;
  description?: string;
  status?: 'active' | 'archived';
  group_id?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  candidate_count?: number;
  interview_count?: number;
}

export interface SectionMember {
  candidate_id: string;
  full_name: string;
  email: string;
  neptun_code?: string | null;
  status?: string;
  created_at?: string;
}

export interface SectionInterviewAssignment {
  id: string;
  section_id: string;
  interview_id: string;
  interview_title?: string;
  difficulty_level?: string;
  estimated_duration_minutes?: number;
  part_b_duration_minutes?: number | null;
  scheduled_at?: string | null;
  completion_deadline?: string;
  candidate_can_view_results?: boolean;
  auto_approve_assessment?: boolean;
  review_mode?: 'manual' | 'auto_approve' | 'auto_review' | string;
  restriction_enabled?: boolean;
  allowed_ip_ranges?: string[];
  allowed_locations?: Array<{
    raw_input: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    normalized_tokens?: string[];
  }>;
  match_mode?: 'any' | 'all' | string;
  enforcement_mode?: 'start_only' | 'continuous' | string;
  fail_action?: 'block' | 'warn' | string;
  interview_type?: 'text' | 'voice' | 'mcq';
  allow_retake?: boolean;
  max_attempts?: number;
  retake_scope?: 'none' | 'part_a' | 'part_b' | 'both' | string;
  available_from?: string | null;
  available_until?: string | null;
  created_at?: string;
}

export const sectionsApi = {
  list: async (params?: { include_archived?: boolean }) => {
    const qs = params?.include_archived ? '?include_archived=true' : '';
    return apiRequest<{ sections: CandidateSection[] }>(`/sections${qs}`);
  },

  create: async (data: { name: string; description?: string }) => {
    return apiRequest<{ section: CandidateSection }>('/sections', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  update: async (id: string, data: { name?: string; description?: string; status?: 'active' | 'archived' }) => {
    return apiRequest<{ section: CandidateSection }>(`/sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  remove: async (id: string, options?: { archive?: boolean }) => {
    const qs = options?.archive ? '?archive=true' : '';
    return apiRequest<{ message: string }>(`/sections/${id}${qs}`, {
      method: 'DELETE'
    });
  },

  listMembers: async (sectionId: string, params?: { q?: string; limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.q) queryParams.set('q', params.q);
    if (typeof params?.limit === 'number') queryParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') queryParams.set('offset', String(params.offset));
    const query = queryParams.toString();
    return apiRequest<{ members: SectionMember[]; total?: number; limit?: number; offset?: number }>(
      `/sections/${sectionId}/members${query ? `?${query}` : ''}`
    );
  },

  listAvailableCandidates: async (
    sectionId: string,
    params?: { q?: string; limit?: number; offset?: number; ids_only?: boolean }
  ) => {
    const queryParams = new URLSearchParams();
    if (params?.q) queryParams.set('q', params.q);
    if (typeof params?.limit === 'number') queryParams.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') queryParams.set('offset', String(params.offset));
    if (params?.ids_only) queryParams.set('ids_only', 'true');
    const query = queryParams.toString();
    return apiRequest<{
      candidates?: Array<{ id: string; full_name: string; email: string; status?: string; neptun_code?: string | null }>;
      candidate_ids?: string[];
      total: number;
      limit: number;
      offset: number;
    }>(`/sections/${sectionId}/available-candidates${query ? `?${query}` : ''}`);
  },

  addMembers: async (sectionId: string, candidateIds: string[]) => {
    const uniqueIds = [...new Set(candidateIds.filter(Boolean))];
    const batchSize = 200;
    let added_count = 0;
    const candidates: string[] = [];

    for (let index = 0; index < uniqueIds.length; index += batchSize) {
      const chunk = uniqueIds.slice(index, index + batchSize);
      const result = await apiRequest<{ added_count: number; candidates: string[] }>(`/sections/${sectionId}/members`, {
        method: 'POST',
        body: JSON.stringify({ candidate_ids: chunk })
      });
      added_count += result.added_count || 0;
      candidates.push(...(result.candidates || []));
    }

    return { added_count, candidates };
  },

  removeMember: async (sectionId: string, candidateId: string) => {
    return apiRequest<{ message: string }>(`/sections/${sectionId}/members/${candidateId}`, {
      method: 'DELETE'
    });
  },

  removeMembers: async (sectionId: string, candidateIds: string[]) => {
    return apiRequest<{ removed: string[]; message: string }>(`/sections/${sectionId}/members/remove`, {
      method: 'POST',
      body: JSON.stringify({ candidate_ids: candidateIds })
    });
  },

  listAssignments: async (sectionId: string) => {
    return apiRequest<{ assignments: SectionInterviewAssignment[] }>(`/sections/${sectionId}/assignments`);
  },

  assignInterview: async (sectionId: string, data: {
    interview_id: string;
    scheduled_at?: string | null;
    completion_deadline?: string | null;
    candidate_can_view_results?: boolean;
    auto_approve_assessment?: boolean;
    review_mode?: 'manual' | 'auto_approve' | 'auto_review' | string;
    restriction_enabled?: boolean;
    allowed_ip_ranges?: string[];
    allowed_locations?: Array<{
      raw_input: string;
      city?: string | null;
      region?: string | null;
      country?: string | null;
      normalized_tokens?: string[];
    }>;
    match_mode?: 'any' | 'all';
    enforcement_mode?: 'start_only' | 'continuous';
    fail_action?: 'block' | 'warn';
    interview_type?: 'text' | 'voice' | 'mcq';
    allow_retake?: boolean;
    max_attempts?: number;
    retake_scope?: 'none' | 'part_a' | 'part_b' | 'both';
  }) => {
    return apiRequest<{ assignment: SectionInterviewAssignment; assigned_candidates: number }>(
      `/sections/${sectionId}/assign-interview`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  },

  removeAssignment: async (sectionId: string, assignmentId: string) => {
    return apiRequest<{ message: string }>(`/sections/${sectionId}/assignments/${assignmentId}`, {
      method: 'DELETE'
    });
  }
};

// =============================================================================
// ASSESSMENTS API
// =============================================================================
export type AssessmentAdminStatus =
  | 'pending'
  | 'pending_review'
  | 'in_review'
  | 'reviewed'
  | 'approved'
  | 'rejected';

export type AssessmentAdminDecision = 'proceed' | 'reject' | 'hold' | 'reinterview';

export interface AssessmentReviewHistoryEntry {
  timestamp: string;
  reviewer_id?: string;
  reviewer_name?: string;
  origin?: 'human' | 'auto_approve' | 'ai' | 'fallback' | string;
  previous_values?: {
    admin_status?: string;
    admin_final_score?: number | null;
    admin_score_modification_reason?: string | null;
    admin_comments?: string | null;
    admin_decision?: string | null;
  };
  changes?: {
    admin_status?: string;
    admin_final_score?: number | null;
    admin_score_modification_reason?: string | null;
    admin_comments?: string | null;
    admin_decision?: string | null;
  };
}

export interface Assessment {
  id: string;
  session_id: string;
  candidate_id: string;
  interview_id: string;
  admin_status: AssessmentAdminStatus | string;
  admin_reviewer_id?: string;
  admin_reviewed_at?: string;
  admin_final_score?: number;
  admin_comments?: string;
  admin_decision?: AssessmentAdminDecision | string;
  admin_score_modification_reason?: string;
  decision_origin?: 'human' | 'auto_approve' | 'ai' | 'fallback' | string | null;
  ai_review_meta?: {
    prompt_version?: string;
    model?: string;
    provider?: string;
    confidence?: number;
    reasoning?: string;
    score?: number;
    fallback_reason?: string;
    decided_at?: string;
  } | Record<string, unknown>;
  review_history?: AssessmentReviewHistoryEntry[];
  created_at: string;
  requires_human_review?: boolean;
  // Joined fields from list API
  candidate_name?: string;
  candidate_email?: string;
  interview_title?: string;
  interview_description?: string;
  integrity_score?: number;
  session_completed_at?: string;
  duration_seconds?: number;
  ai_score?: number;
  ai_confidence?: number;
  recommendation?: string;
  reviewer_name?: string;
  // Additional fields from get API
  transcript?: unknown;
  integrity_events?: unknown;
  started_at?: string;
  completed_at?: string;
  ai_overall_score?: number;
  confidence?: number;
  criterion_scores?: Record<string, { score: number; feedback: string }> | Array<{ id?: string; name?: string; score?: number; feedback?: string }>;
  strengths?: string[];
  weaknesses?: string[];
  detailed_feedback?: string;
  recommendation_reasoning?: string;
  ai_integrity_report?: unknown;
  mcq_score_percent?: number;
  mcq_correct_count?: number;
  mcq_total_questions?: number;
  mcq_attempt_id?: string;
  candidate_can_view_results?: boolean;
  results_hidden?: boolean;
  result_visibility_message?: string;
  interview_placement_id?: string | null;
  placement_rank?: number | null;
  placement_cohort_size?: number | null;
  placement_percentile?: number | null;
  placement?: AssessmentPlacementInfo | null;
  placement_result?: AssessmentPlacementResult | null;
  assignment_id?: string | null;
  retake_scope?: string | null;
  allow_retake?: boolean;
  attempt_count?: number | null;
  max_attempts?: number | null;
  assignment_status?: string | null;
  proctoring_summary?: AssessmentProctoringSummary | null;
}

export interface AssessmentMcqQuestion {
  order_index: number;
  section_label?: string | null;
  options?: unknown;
  correct_index?: number | null;
  question_text?: string | null;
  topic?: string | null;
  difficulty?: string | null;
  explanation?: string | null;
  question_content?: McqContentBlock[] | null;
  option_content?: McqContentBlock[][] | null;
  explanation_content?: McqContentBlock[] | null;
  figure_metadata?: McqFigureMetadata[] | null;
  selected_index?: number | null;
  is_correct?: boolean | null;
  flagged?: boolean | null;
  answered_at?: string | null;
}

export interface AssessmentPlacementInfo {
  id: string;
  name: string;
  code: string;
  assessment_mode?: string | null;
}

export interface AssessmentPlacementResult {
  placement_id?: string | null;
  placement_name?: string | null;
  assessment_mode?: string | null;
  rank?: number | null;
  cohort_size?: number | null;
  percentile?: number | null;
  mcq_score: number | null;
  mcq_max_score: number | null;
  oral_score: number | null;
  oral_max_score: number | null;
  final_score: number | null;
  max_score: number | null;
  completed?: boolean;
  completed_at?: string | null;
  question_scores?: PlacementQuestionScore[] | null;
  transfer_history?: unknown[] | null;
}

export interface AssessmentProctoringSummary {
  event_count: number;
  max_severity: 'low' | 'medium' | 'high' | 'critical' | null;
  unreviewed_count: number;
}

export interface AssessmentAssignmentInfo {
  id: string;
  candidate_can_view_results: boolean;
  retake_scope: string;
  allow_retake: boolean;
  attempt_count: number | null;
  max_attempts: number | null;
  status: string | null;
}

export interface AssessmentReportCriterionEntry {
  score?: number;
  feedback?: string;
}

export interface AssessmentReport {
  metadata: {
    report_version: string;
    assessment_id: string;
    generated_at: string;
    generated_by_role: string;
    generated_by_user_id: string;
    format: 'json' | 'pdf' | string;
  };
  visibility: {
    candidate_can_view_results: boolean;
    results_hidden: boolean;
    message?: string | null;
  };
  candidate: {
    id: string;
    full_name?: string | null;
    email?: string | null;
  };
  interview: {
    id: string;
    title?: string | null;
    description?: string | null;
  };
  session: {
    id: string;
    started_at?: string | null;
    completed_at?: string | null;
    duration_seconds?: number | null;
    integrity_score?: number | null;
    transcript_turn_count?: number;
  };
  scoring: {
    admin_final_score?: number | null;
    ai_overall_score?: number | null;
    ai_confidence?: number | null;
    mcq: {
      attempt_id?: string | null;
      score_percent?: number | null;
      correct_count?: number | null;
      total_questions?: number | null;
    };
  };
  recommendation: {
    value?: string | null;
    reasoning?: string | null;
  };
  review: {
    admin_status?: string | null;
    admin_decision?: string | null;
    reviewer_name?: string | null;
    reviewed_at?: string | null;
    comments?: string | null;
    score_modification_reason?: string | null;
    history?: any[];
  };
  criterion_scores: Record<string, AssessmentReportCriterionEntry>;
  strengths: string[];
  weaknesses: string[];
  detailed_feedback?: string | null;
  ai_integrity_report?: any;
}

export type EvaluationDraftPurpose = 'final_evaluation' | 'integrity_analysis';
export interface EvaluationDraft {
  id?: string; purpose: EvaluationDraftPurpose; state: 'none' | 'pending' | 'accepted' | 'stale'; template_version?: string; definition_version?:number|null; processing?: 'queued'|'running'|'retry'|'succeeded'|'failed'|'timed_out'|'cancelled'|'missing';
  result?: {status:string; summary:string; confidence:number|null; overall_score?:number; risk_level?:string; strengths?:string[]; weaknesses?:string[]; indicators?:string[]; limitations:string[]; evidence:Array<{entry_index?:number;event_index?:number;reason:string}>};
  coverage?: {transcript_total:number;transcript_included:number;transcript_truncated:number;integrity_events_total:number;integrity_events_included:number;material_characters:number;material_characters_included:number};
}
// How an accepted advisory draft related to a human review ledger entry. Read-only; never a grade or release.
export type DraftReconciliationRelation = 'agreement' | 'disagreement' | 'insufficient_evidence' | 'not_comparable' | 'advisory_only';
export interface DraftReconciliation {
  id: string; proposal_id: string; review_history_index: number; purpose: EvaluationDraftPurpose; evidence_match: 'current'|'stale'|'unknown'; relation: DraftReconciliationRelation;
  tolerance_points: number; proposal_score: number|null; authoritative_score: number|null; score_delta: number|null; proposal_risk_level: string|null; authoritative_status: string|null; authoritative_decision: string|null; created_at: string;
  criterion_comparison?: {status:'not_recorded'|'compared'|'insufficient'; items:Array<{criterion_id:string; proposal_score:number; authoritative_score:number|null; score_delta:number|null; relation:'agreement'|'disagreement'|'not_comparable'}>};
}
export interface DraftReconciliationList { policy: {authority:string; advisory_only:boolean; tolerance_points:number; missing_record_means:string}; items: DraftReconciliation[]; }

export const assessmentsApi = {
  list: async (params?: {
    admin_status?: AssessmentAdminStatus | string;
    limit?: number;
    offset?: number;
    q?: string;
    interview_id?: string;
    candidate_id?: string;
    admin_decision?: AssessmentAdminDecision | string;
    requires_human_review?: boolean;
    from_date?: string;
    to_date?: string;
    sort?: 'created_at' | 'admin_status' | 'ai_score' | 'final_score' | 'integrity_score' | string;
    order?: 'asc' | 'desc';
  }) => {
    const query = new URLSearchParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue;
        query.set(key, String(value));
      }
    }
    const qs = query.toString();
    return apiRequest<{ assessments: Assessment[]; total?: number; limit?: number; offset?: number }>(
      `/assessments${qs ? `?${qs}` : ''}`
    );
  },

  get: async (id: string) => {
    return apiRequest<{
      assessment: Assessment;
      mcq_attempt?: McqAttempt;
      mcq_questions?: AssessmentMcqQuestion[];
      placement?: AssessmentPlacementInfo | null;
      placement_result?: AssessmentPlacementResult | null;
      placement_rank?: number | null;
      placement_cohort_size?: number | null;
      proctoring_summary?: AssessmentProctoringSummary | null;
      assignment?: AssessmentAssignmentInfo | null;
      results_hidden?: boolean;
      message?: string;
    }>(`/assessments/${id}`);
  },

  getReport: async (id: string) => {
    return apiRequest<{ report: AssessmentReport }>(`/assessments/${id}/report?format=json`);
  },

  downloadReportPdf: async (id: string) => {
    return apiRequestBlob(`/assessments/${id}/report?format=pdf`, {
      method: 'GET'
    });
  },

  review: async (id: string, data: {
    admin_status?: AssessmentAdminStatus;
    admin_final_score?: number;
    admin_score_modification_reason?: string;
    admin_comments?: string;
    admin_decision?: AssessmentAdminDecision;
  }) => {
    return apiRequest<{ assessment: Assessment }>(`/assessments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  evaluationProposal: (id: string,purpose: EvaluationDraftPurpose) => apiRequest<EvaluationDraft>(`/assessments/${id}/evaluation-proposals?purpose=${purpose}`, {suppressGlobalErrorRedirect:true}),
  queueEvaluationProposal: (id: string,purpose: EvaluationDraftPurpose,request_id: string) => apiRequest<EvaluationDraft>(`/assessments/${id}/evaluation-proposal-jobs`, {method:'POST',body:JSON.stringify({purpose,request_id}),suppressGlobalErrorRedirect:true}),
  evaluationProposalReconciliations: (id: string) => apiRequest<DraftReconciliationList>(`/assessments/${id}/evaluation-proposals/reconciliations`, {suppressGlobalErrorRedirect:true}),
  generateEvaluationProposal: (id: string,purpose: EvaluationDraftPurpose,request_id: string) => apiRequest<EvaluationDraft>(`/assessments/${id}/evaluation-proposals`, {method:'POST',body:JSON.stringify({purpose,request_id}),suppressGlobalErrorRedirect:true}),

  addAiEvaluation: async (id: string, evaluation: Record<string,unknown>,submission?:{request_id:string;expected_evidence_revision?:number}) => {
    return apiRequest<{ evaluation: any;replayed?:boolean;evidence_revision?:string }>(`/assessments/${id}/ai-evaluation`, {
      method: 'POST',
      body: JSON.stringify({...evaluation,...submission}),
    });
  },
};

// =============================================================================
// INTERVIEW FEEDBACK API
// =============================================================================
export interface InterviewFeedbackRecord {
  id: string;
  group_id: string;
  interview_id: string;
  session_id?: string | null;
  assignment_id?: string | null;
  candidate_id: string;
  overall_rating: number;
  interview_difficulty?: number | null;
  question_clarity?: number | null;
  platform_experience?: number | null;
  fairness_rating?: number | null;
  comment_positive?: string | null;
  comment_improvement?: string | null;
  recommend_platform?: boolean | null;
  submitted_at: string;
  created_at?: string;
  updated_at?: string;
  candidate_name?: string;
  candidate_email?: string;
  interview_title?: string;
  interview_description?: string;
  session_started_at?: string | null;
  session_completed_at?: string | null;
  has_comments?: boolean;
}

export interface CandidateFeedbackPayload {
  interview_id: string;
  session_id?: string;
  assignment_id?: string;
  overall_rating: number;
  interview_difficulty?: number;
  question_clarity?: number;
  platform_experience?: number;
  fairness_rating?: number;
  comment_positive?: string;
  comment_improvement?: string;
  recommend_platform?: boolean;
}

export interface InterviewFeedbackAdminFilters {
  start_date?: string;
  end_date?: string;
  interview_id?: string;
  candidate_id?: string;
  min_rating?: number;
  max_rating?: number;
  has_comments?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface InterviewFeedbackSummary {
  total_feedback: number;
  average_rating: number;
  recommendation_rate: number;
  comments_count: number;
}

export interface InterviewFeedbackListResponse {
  feedback: InterviewFeedbackRecord[];
  total: number;
  limit: number;
  offset: number;
  summary: InterviewFeedbackSummary;
  filters: {
    start_date: string | null;
    end_date: string | null;
    interview_id: string | null;
    candidate_id: string | null;
    min_rating: number | null;
    max_rating: number | null;
    has_comments: boolean | null;
    search: string | null;
  };
}

function toQueryString(params: object): string {
  const query = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.set(key, String(value));
  });
  return query.toString();
}

export const feedbackApi = {
  submit: async (data: CandidateFeedbackPayload) => {
    return apiRequest<{ message: string; feedback_id: string; submitted_at: string }>('/feedback', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  listAdmin: async (filters?: InterviewFeedbackAdminFilters) => {
    const query = toQueryString(filters || {});
    return apiRequest<InterviewFeedbackListResponse>(`/feedback/admin${query ? `?${query}` : ''}`);
  },

  getAdmin: async (id: string) => {
    return apiRequest<{ feedback: InterviewFeedbackRecord }>(`/feedback/admin/${id}`);
  },

  exportCsv: async (filters?: InterviewFeedbackAdminFilters) => {
    const query = toQueryString(filters || {});
    return apiRequestBlob(`/feedback/admin/export.csv${query ? `?${query}` : ''}`, {
      method: 'GET'
    });
  },

  download: async (id: string) => {
    return apiRequestBlob(`/feedback/admin/${id}/download`, {
      method: 'GET'
    });
  }
};

// =============================================================================
// AUDIT API
// =============================================================================
export const auditApi = {
  list: async (params?: { 
    action?: string; 
    user_id?: string; 
    resource_type?: string;
    from_date?: string;
    to_date?: string;
    limit?: number; 
    offset?: number 
  }) => {
    const queryParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      queryParams.set(key, String(value));
    });

    const query = queryParams.toString();
    return apiRequest<{ logs: any[]; total: number; limit: number; offset: number }>(`/audit?${query}`);
  },

  getActions: async () => {
    return apiRequest<{ actions: { action: string; count: number }[] }>('/audit/actions');
  },

  getStats: async () => {
    return apiRequest<{
      last_24h: Array<{ action: string; count: number | string }>;
      last_7_days: Array<{ date: string; count: number | string }>;
      active_users: Array<{
        user_id: string;
        full_name: string;
        email: string;
        action_count: number | string;
      }>;
    }>('/audit/stats');
  },

  getIntegrity: async (limit?: number) => {
    const query = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
    return apiRequest<{
      scanned: number;
      signed: number;
      unsigned: number;
      valid: number;
      invalid: number;
      broken_links: number;
      first_issue?: { id: string; reason: string } | null;
      is_valid: boolean;
    }>(`/audit/integrity${query}`);
  },

  purgeLlm: async (params: {
    session_id?: string;
    from_date?: string;
    to_date?: string;
    dry_run?: boolean;
  }) => {
    const payload = Object.fromEntries(
      Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null)
    );

    return apiRequest<{
      matched: number;
      deleted: number;
      dry_run: boolean;
      filters: {
        session_id: string | null;
        from_date: string | null;
        to_date: string | null;
      };
    }>('/audit/llm/purge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  log: async (action: string, resourceTypeOrDetails?: string | object, resourceId?: string, details?: any) => {
    // Handle both calling conventions:
    // 1. log(action, resourceType, resourceId, details)
    // 2. log(action, details) - shorthand where details is passed as second arg
    let finalResourceType: string | undefined;
    let finalResourceId: string | undefined;
    let finalDetails: any;

    if (typeof resourceTypeOrDetails === 'object' && resourceTypeOrDetails !== null) {
      // Called as: log(action, details)
      finalResourceType = undefined;
      finalResourceId = undefined;
      finalDetails = resourceTypeOrDetails;
    } else {
      // Called as: log(action, resourceType, resourceId, details)
      finalResourceType = resourceTypeOrDetails;
      finalResourceId = resourceId;
      finalDetails = details;
    }

    try {
      return await apiRequest('/audit', {
        method: 'POST',
        body: JSON.stringify({
          action,
          resource_type: finalResourceType,
          resource_id: finalResourceId,
          details: finalDetails
        }),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown audit error';
      console.warn('[auditApi] Non-fatal audit logging failure:', message);
      return { accepted: false, error: message };
    }
  },
};

// =============================================================================
// DASHBOARD API
// =============================================================================
export interface DashboardStats {
  interviews: {
    total: number;
    active: number;
    draft: number;
    archived: number;
    change: string;
  };
  candidates: {
    total: number;
    active: number;
    inactive: number;
    suspended: number;
    change: string;
  };
  assessments: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    requires_discussion: number;
    change: string;
  };
  sessions: {
    total: number;
    completed: number;
    in_progress: number;
    scheduled: number;
    expired: number;
    cancelled: number;
  };
  completion_rate: number;
}

export interface RecentActivity {
  id: string;
  type: 'interview' | 'candidate' | 'assessment' | 'session' | 'system';
  message: string;
  time: string;
  timestamp: string;
  user: string;
  action: string;
  resource_type?: string | null;
  resource_id?: string | null;
  details?: {
    title?: string | null;
    candidate_name?: string | null;
    email?: string | null;
  } | null;
}

export interface UpcomingInterview {
  assignment_id: string;
  scheduled_at: string | null;
  available_from?: string | null;
  available_until?: string | null;
  start_at?: string | null;
  status: string;
  interview_id: string;
  interview_title: string;
  estimated_duration_minutes: number;
  part_b_duration_minutes?: number | null;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
}

export interface PendingReview {
  id: string;
  created_at: string;
  admin_status: string;
  candidate_id: string;
  started_at: string;
  completed_at: string;
  integrity_score: number;
  interview_title: string;
  candidate_name: string;
  candidate_email: string;
  ai_score: number;
  recommendation: string;
}

export interface DropoffAnalytics {
  window_days: number;
  assignments: {
    total: number;
    pending: number;
    scheduled: number;
    in_progress: number;
    completed: number;
    cancelled: number;
    completion_rate: number;
    dropoff_rate: number;
  };
  sessions: {
    started: number;
    completed: number;
    expired: number;
    dropoff_rate: number;
  };
}

export interface TrendPoint {
  day: string;
  assignments_created: number;
  assignments_scheduled: number;
  assignments_completed: number;
  sessions_started: number;
  sessions_completed: number;
  assessments_pending: number;
  assessments_reviewed: number;
}

export interface CohortComparison {
  cohort_id: string;
  cohort_name: string;
  candidate_count: number;
  assignment_count: number;
  completed_count: number;
  completion_rate: number;
}

export const dashboardApi = {
  getStats: async () => {
    return apiRequest<DashboardStats>('/dashboard/stats');
  },

  getRecentActivity: async (limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    return apiRequest<{ activities: RecentActivity[] }>(`/dashboard/recent-activity${query}`);
  },

  getUpcomingInterviews: async (limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    return apiRequest<{ upcoming: UpcomingInterview[] }>(`/dashboard/upcoming-interviews${query}`);
  },

  getPendingReviews: async (limit?: number) => {
    const query = limit ? `?limit=${limit}` : '';
    return apiRequest<{ pending: PendingReview[] }>(`/dashboard/pending-reviews${query}`);
  },

  getDropoffAnalytics: async (days: number = 30) => {
    return apiRequest<DropoffAnalytics>(`/dashboard/analytics/dropoff?days=${encodeURIComponent(String(days))}`);
  },

  getCompletionDistribution: async (days: number = 90) => {
    return apiRequest<{ window_days: number; distribution: Array<{ bucket: string; count: number }> }>(
      `/dashboard/analytics/completion-distribution?days=${encodeURIComponent(String(days))}`
    );
  },

  getTrends: async (days: number = 30) => {
    return apiRequest<{ window_days: number; points: TrendPoint[] }>(
      `/dashboard/analytics/trends?days=${encodeURIComponent(String(days))}`
    );
  },

  getCohortComparison: async (params?: { group_by?: 'interview' | 'section'; days?: number }) => {
    const search = new URLSearchParams();
    if (params?.group_by) search.set('group_by', params.group_by);
    if (params?.days) search.set('days', String(params.days));
    const query = search.toString();

    return apiRequest<{
      window_days: number;
      group_by: 'interview' | 'section';
      cohorts: CohortComparison[];
      role_comparison: Array<{
        role: string;
        assignment_count: number;
        completed_count: number;
        completion_rate: number;
      }>;
    }>(`/dashboard/analytics/cohort-comparison${query ? `?${query}` : ''}`);
  },
};

// =============================================================================
// ADMIN TOOLS API
// =============================================================================
export const adminToolsApi = {
  listFilterPresets: async (scope?: string) => {
    const query = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    return apiRequest<{ presets: any[] }>(`/admin-tools/filter-presets${query}`);
  },

  saveFilterPreset: async (data: {
    preset_scope: string;
    preset_name: string;
    filters: Record<string, any>;
    is_default?: boolean;
  }) => {
    return apiRequest<{ preset: any }>('/admin-tools/filter-presets', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateFilterPreset: async (id: string, data: {
    preset_name?: string;
    filters?: Record<string, any>;
    is_default?: boolean;
  }) => {
    return apiRequest<{ preset: any }>(`/admin-tools/filter-presets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteFilterPreset: async (id: string) => {
    return apiRequest<{ ok: boolean }>(`/admin-tools/filter-presets/${id}`, { method: 'DELETE' });
  },

  resetFilterPresets: async (scope?: string) => {
    return apiRequest<{
      ok: boolean;
      deleted_count: number;
      scope: string;
      message?: string;
    }>('/admin-tools/filter-presets/reset', {
      method: 'POST',
      body: JSON.stringify(scope ? { scope } : {})
    });
  },

  listInterviewTemplates: async (includePublic: boolean = true) => {
    return apiRequest<{ templates: any[] }>(
      `/admin-tools/interview-templates?include_public=${encodeURIComponent(String(includePublic))}`
    );
  },

  createInterviewTemplate: async (data: {
    name: string;
    description?: string;
    template_data: Record<string, any>;
    is_public?: boolean;
  }) => {
    return apiRequest<{ template: any }>('/admin-tools/interview-templates', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateInterviewTemplate: async (id: string, data: {
    name?: string;
    description?: string;
    template_data?: Record<string, any>;
    is_public?: boolean;
  }) => {
    return apiRequest<{ template: any }>(`/admin-tools/interview-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteInterviewTemplate: async (id: string) => {
    return apiRequest<{ ok: boolean }>(`/admin-tools/interview-templates/${id}`, { method: 'DELETE' });
  },

  importCandidates: async (data: {
    records?: Array<Record<string, any>>;
    content?: string;
    format?: 'json' | 'csv';
    default_interview_id?: string;
    default_interview_type?: 'text' | 'voice' | 'mcq';
    default_scheduled_at?: string;
  }) => {
    return apiRequest<{
      total: number;
      created_users: number;
      reused_users: number;
      assigned: number;
      invitations_dispatched?: number;
      errors: Array<{ row: number; email?: string; error: string }>;
    }>('/admin-tools/candidates/import', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  exportCandidates: async (params?: {
    interview_id?: string;
    status?: string;
    format?: 'json' | 'csv';
  }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const format = params?.format || 'json';

    if (format === 'csv') {
      const response = await fetch(`${API_URL}/api/admin-tools/candidates/export?${query}`, {
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          'x-locale': getEffectiveBcp47Locale(),
          ...(activeGroupId ? { 'x-group-id': activeGroupId } : {})
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(error.error || 'Export failed');
      }

      return { csv: await response.text() };
    }

    return apiRequest<{ records: any[]; count: number }>(`/admin-tools/candidates/export?${query}`);
  },

  bulkScheduleAssignments: async (data: {
    assignment_ids: string[];
    scheduled_at?: string;
    interview_type?: 'text' | 'voice' | 'mcq';
    status?: 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  }) => {
    return apiRequest<{ updated_count: number; assignments: any[] }>('/admin-tools/assignments/bulk-schedule', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
};

// =============================================================================
// NOTIFICATIONS API
// =============================================================================
export type NotificationChannel = 'email' | 'in_app';
export type NotificationStatus = 'pending' | 'sent' | 'failed' | 'cancelled';
export type NotificationPriority = 'low' | 'normal' | 'high' | 'critical';
export type NotificationDigestMode = 'immediate' | 'daily';

export interface NotificationRecord {
  id: string;
  user_id: string;
  group_id?: string | null;
  assignment_id?: string | null;
  channel: NotificationChannel;
  notification_type: string;
  status: NotificationStatus;
  priority?: NotificationPriority;
  timezone?: string;
  subject?: string | null;
  body?: string | null;
  metadata?: Record<string, any>;
  scheduled_for: string;
  sent_at?: string | null;
  is_read?: boolean;
  read_at?: string | null;
  archived_at?: string | null;
  error_message?: string | null;
  attempt_count?: number;
  max_attempts?: number;
  source_event?: string | null;
  actor_user_id?: string | null;
  created_at?: string;
  updated_at?: string;
  full_name?: string;
  email?: string;
  deduped?: boolean;
  suppressed?: boolean;
}

export interface NotificationPreference {
  user_id: string;
  group_id: string;
  notification_type: string;
  channel: NotificationChannel;
  enabled: boolean;
  quiet_hours_start: number | null;
  quiet_hours_end: number | null;
  digest_mode: NotificationDigestMode;
  created_at?: string;
  updated_at?: string;
}

export interface NotificationDeliverySettings {
  group_id: string;
  report_email_mode: 'link' | 'attachment';
  report_link_ttl_minutes: number;
  max_attachment_size_mb: number;
  updated_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export type AdminMessageAudienceKind = 'users' | 'role' | 'section' | 'interview';
export type AdminMessageRole = 'candidate' | 'invigilator' | 'admin' | 'super_admin';

export interface AdminMessageAudience {
  kind: AdminMessageAudienceKind;
  user_ids?: string[];
  role?: AdminMessageRole;
  section_id?: string;
  interview_id?: string;
}

export interface AdminMessageSendRequest {
  subject: string;
  body: string;
  audience: AdminMessageAudience;
  channels?: NotificationChannel[];
  priority?: NotificationPriority;
  assignment_id?: string;
  dry_run?: boolean;
}

export interface AdminMessageSendResult {
  send_id: string;
  recipient_count: number;
  created_count: number;
  suppressed_count: number;
  notifications?: string[];
  dry_run?: boolean;
  max_recipients?: number;
}

export const notificationsApi = {
  list: async (params?: {
    status?: NotificationStatus;
    channel?: NotificationChannel;
    notification_type?: string;
    is_read?: boolean;
    include_archived?: boolean;
    all_groups?: boolean;
    user_id?: string;
    assignment_id?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        queryParams.set(key, String(value));
      });
    }
    const query = queryParams.toString();
    return apiRequest<{ notifications: NotificationRecord[] }>(`/notifications?${query}`);
  },

  unreadCount: async (params?: { user_id?: string; all_groups?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        queryParams.set(key, String(value));
      });
    }
    const query = queryParams.toString();
    return apiRequest<{ unread_count: number }>(`/notifications/unread-count${query ? `?${query}` : ''}`);
  },

  markRead: async (id: string) => {
    return apiRequest<{ notification: NotificationRecord }>(`/notifications/${id}/read`, {
      method: 'PATCH'
    });
  },

  markAllRead: async (data?: { user_id?: string; all_groups?: boolean }) => {
    return apiRequest<{ updated_count: number }>('/notifications/read-all', {
      method: 'PATCH',
      body: JSON.stringify(data || {})
    });
  },

  archive: async (id: string) => {
    return apiRequest<{ notification: NotificationRecord }>(`/notifications/${id}/archive`, {
      method: 'PATCH'
    });
  },

  getPreferences: async (params?: { user_id?: string }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        queryParams.set(key, String(value));
      });
    }
    const query = queryParams.toString();
    return apiRequest<{
      user_id: string;
      group_id: string;
      preferences: NotificationPreference[];
    }>(`/notifications/preferences${query ? `?${query}` : ''}`);
  },

  savePreferences: async (data: {
    user_id?: string;
    quiet_hours_start?: number | null;
    quiet_hours_end?: number | null;
    digest_mode?: NotificationDigestMode;
    preferences: Array<{
      notification_type: string;
      channel: NotificationChannel;
      enabled: boolean;
      quiet_hours_start?: number | null;
      quiet_hours_end?: number | null;
      digest_mode?: NotificationDigestMode;
    }>;
  }) => {
    return apiRequest<{
      user_id: string;
      group_id: string;
      preferences: NotificationPreference[];
    }>('/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  resetPreferences: async (data?: { user_id?: string }) => {
    return apiRequest<{
      user_id: string;
      group_id: string;
      preferences: NotificationPreference[];
      message?: string;
    }>('/notifications/preferences/reset', {
      method: 'POST',
      body: JSON.stringify(data || {})
    });
  },

  schedule: async (data: {
    user_id: string;
    assignment_id?: string;
    channel?: NotificationChannel;
    notification_type: string;
    timezone?: string;
    priority?: NotificationPriority;
    dedupe_key?: string;
    source_event?: string;
    subject?: string;
    body?: string;
    metadata?: Record<string, any>;
    scheduled_for?: string;
  }) => {
    return apiRequest<{ notification: NotificationRecord; suppressed?: boolean }>('/notifications/schedule', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  sendAdminMessage: async (data: AdminMessageSendRequest) => {
    return apiRequest<AdminMessageSendResult>('/notifications/admin-messages', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  createAssignmentReminders: async (assignmentId: string, includeCalendarInvite: boolean = true) => {
    return apiRequest<{ assignment_id: string; notifications: NotificationRecord[]; created_count: number }>(
      `/notifications/assignments/${assignmentId}/reminders`,
      {
        method: 'POST',
        body: JSON.stringify({ include_calendar_invite: includeCalendarInvite })
      }
    );
  },

  sendAssignmentStatusUpdate: async (assignmentId: string, status: string, message?: string) => {
    return apiRequest<{ notification: NotificationRecord }>(`/notifications/assignments/${assignmentId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, message })
    });
  },

  requeue: async (id: string) => {
    return apiRequest<{ notification: NotificationRecord }>(`/notifications/${id}/requeue`, {
      method: 'POST'
    });
  },

  processDue: async (limit: number = 25, allGroups: boolean = false) => {
    return apiRequest<{ processed: number; sent: number; failed: number; retried: number }>('/notifications/process-due', {
      method: 'POST',
      body: JSON.stringify({ limit, all_groups: allGroups })
    });
  },

  getDeliverySettings: async () => {
    return apiRequest<{ settings: NotificationDeliverySettings }>('/notifications/settings/delivery');
  },

  saveDeliverySettings: async (data: {
    report_email_mode: 'link' | 'attachment';
    report_link_ttl_minutes: number;
    max_attachment_size_mb: number;
  }) => {
    return apiRequest<{ settings: NotificationDeliverySettings }>('/notifications/settings/delivery', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
};

// =============================================================================
// AI VOICE STUDIO API
// =============================================================================
export interface VoiceCatalogEntry {
  id: string;
  provider: string;
  voice_id: string;
  display_name: string;
  language_code: string;
  accent?: string | null;
  gender?: string | null;
  speaking_style?: string | null;
  short_description?: string | null;
  quality_tier?: 'high' | 'premium' | 'fallback' | string;
  quality_score?: number | null;
  metadata?: Record<string, any>;
  capabilities?: {
    neural?: boolean;
    rate?: boolean;
    pitch?: boolean;
    style?: boolean;
    ssml?: boolean;
  };
  quality_indicators?: string[];
}

export interface VoiceSettingRecord {
  id?: string | null;
  scope_type?: 'global' | 'interview' | 'fallback' | string;
  interview_id?: string | null;
  provider: string;
  provider_voice_id: string;
  language_code: string;
  rate: number;
  pitch: number;
  style?: string;
  use_ssml?: boolean;
  ssml_template?: string;
  punctuation_enhanced?: boolean;
  metadata?: Record<string, any>;
}

export interface VoicePreviewResponse {
  preview: {
    provider: string;
    voice_id: string;
    mime_type: string;
    audio_base64: string;
    generated_at: string;
    cache_hit: boolean;
    cache_ttl_ms: number;
  };
}

export interface EffectiveVoiceResponse {
  interview_id: string;
  interview_language?: string | null;
  preferred_language_code?: string | null;
  language_match?: boolean;
  language_adjusted?: boolean;
  warning?: string | null;
  effective: {
    source: 'interview' | 'global' | 'fallback' | 'language_match' | string;
    setting: VoiceSettingRecord;
    resolution_order: string[];
  };
}

export interface RuntimeVoiceSynthesisResponse {
  interview_id: string;
  source: 'interview' | 'global' | 'fallback' | string;
  fallback_used: boolean;
  voice: VoiceSettingRecord;
  mime_type: string;
  audio_base64: string;
  attempts: Array<{
    label: string;
    provider: string;
    voice_id: string;
    status: 'success' | 'failed';
    error?: string;
    code?: string | null;
  }>;
}

export interface VoiceProviderConfigSummary {
  id?: string;
  provider: string;
  base_url: string;
  model: string;
  is_active: boolean;
  metadata?: Record<string, any>;
  has_api_key: boolean;
  api_key_last4?: string | null;
  source?: 'db' | 'env' | string;
  updated_at?: string | null;
  created_at?: string | null;
}

export interface VoiceProviderConfigTestResponse {
  connected: boolean;
  provider: string;
  base_url: string;
  model: string;
  has_api_key: boolean;
  latency_ms?: number | null;
  model_available?: boolean;
  available_models?: string[];
  tested_at?: string;
  error?: string;
}

export const voiceStudioApi = {
  listCatalog: async (params?: {
    search?: string;
    language?: string;
    accent?: string;
    gender?: string;
    speaking_style?: string;
    provider?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = toQueryString(params || {});
    return apiRequest<{ voices: VoiceCatalogEntry[]; total: number; limit: number; offset: number }>(
      `/voices/catalog${query ? `?${query}` : ''}`
    );
  },

  preview: async (data: {
    provider: string;
    voice_id: string;
    language_code?: string;
    text: string;
    rate?: number;
    pitch?: number;
    style?: string;
    use_ssml?: boolean;
    ssml_template?: string;
    ssml_text?: string;
    punctuation_enhanced?: boolean;
    metadata?: Record<string, any>;
  }) => {
    return apiRequest<VoicePreviewResponse>('/voices/preview', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getProviderConfig: async (provider?: string) => {
    const query = toQueryString({ provider });
    return apiRequest<{ config: VoiceProviderConfigSummary }>(
      `/voices/provider-config${query ? `?${query}` : ''}`
    );
  },

  saveProviderConfig: async (data: {
    provider: string;
    base_url: string;
    model: string;
    api_key?: string;
    clear_api_key?: boolean;
    is_active?: boolean;
    metadata?: Record<string, any>;
  }) => {
    return apiRequest<{ config: VoiceProviderConfigSummary }>('/voices/provider-config', {
      method: 'PUT',
      body: JSON.stringify({
        provider: data.provider,
        base_url: data.base_url,
        model: data.model,
        api_key: data.api_key,
        clear_api_key: data.clear_api_key,
        is_active: data.is_active,
        metadata: data.metadata
      })
    });
  },

  testProviderConfig: async (data?: {
    provider?: string;
    base_url?: string;
    model?: string;
    api_key?: string;
  }) => {
    return apiRequest<VoiceProviderConfigTestResponse>('/voices/provider-config/test', {
      method: 'POST',
      body: JSON.stringify({
        provider: data?.provider,
        base_url: data?.base_url,
        model: data?.model,
        api_key: data?.api_key
      })
    });
  },

  clearProviderKey: async (provider: string, base_url: string, model: string) => {
    return apiRequest<{ config: VoiceProviderConfigSummary }>('/voices/provider-config', {
      method: 'PUT',
      body: JSON.stringify({
        provider,
        base_url,
        model,
        clear_api_key: true
      })
    });
  },

  getGlobalSetting: async () => {
    return apiRequest<{
      setting: VoiceSettingRecord | null;
      fallback: VoiceSettingRecord;
    }>('/voices/settings/global');
  },

  saveGlobalSetting: async (data: VoiceSettingRecord) => {
    return apiRequest<{ setting: VoiceSettingRecord }>('/voices/settings/global', {
      method: 'PUT',
      body: JSON.stringify({
        provider: data.provider,
        voice_id: data.provider_voice_id,
        language_code: data.language_code,
        rate: data.rate,
        pitch: data.pitch,
        style: data.style,
        use_ssml: data.use_ssml,
        ssml_template: data.ssml_template,
        punctuation_enhanced: data.punctuation_enhanced,
        metadata: data.metadata
      })
    });
  },

  clearGlobalSetting: async () => {
    return apiRequest<{ deleted: boolean; message: string }>('/voices/settings/global', {
      method: 'DELETE'
    });
  },

  getInterviewSetting: async (interviewId: string) => {
    return apiRequest<{ setting: VoiceSettingRecord | null }>(`/voices/settings/interviews/${interviewId}`);
  },

  saveInterviewSetting: async (interviewId: string, data: VoiceSettingRecord) => {
    return apiRequest<{ setting: VoiceSettingRecord }>(`/voices/settings/interviews/${interviewId}`, {
      method: 'PUT',
      body: JSON.stringify({
        provider: data.provider,
        voice_id: data.provider_voice_id,
        language_code: data.language_code,
        rate: data.rate,
        pitch: data.pitch,
        style: data.style,
        use_ssml: data.use_ssml,
        ssml_template: data.ssml_template,
        punctuation_enhanced: data.punctuation_enhanced,
        metadata: data.metadata
      })
    });
  },

  clearInterviewSetting: async (interviewId: string) => {
    return apiRequest<{ deleted: boolean; message: string }>(`/voices/settings/interviews/${interviewId}`, {
      method: 'DELETE'
    });
  },

  getEffectiveVoice: async (interviewId: string) => {
    return apiRequest<EffectiveVoiceResponse>(`/voices/effective/${interviewId}`);
  },

  synthesizeInterviewSpeech: async (interviewId: string, data: { text: string; ssml_text?: string; language_code?: string }, options: { signal?: AbortSignal } = {}) => {
    return apiRequest<RuntimeVoiceSynthesisResponse>(`/voices/interviews/${interviewId}/speak`, {
      method: 'POST',
      signal: options.signal,
      body: JSON.stringify(data)
    });
  }
};

// =============================================================================
// PLACEMENT ASSESSMENT API (UD Informatics)
// =============================================================================
export interface PlacementProgrammingBackgroundRow {
  language: string;
  where_learned: 'school' | 'extracurricular' | 'independent' | 'other';
  duration: string;
}

export interface PlacementDeclarationAnswers {
  accepts_algorithmic_thinking_course?: boolean;
  accepts_advanced_group?: boolean;
  no_programming_background?: boolean;
  programming_background?: PlacementProgrammingBackgroundRow[];
  [key: string]: unknown;
}

export type DeclarationQuestionType = 'boolean' | 'checkbox' | 'text' | 'enum' | 'repeating_group';

export interface DeclarationEnumOption {
  value: string;
  label_en?: string;
  label_hu?: string;
}

export interface DeclarationQuestionField {
  key: string;
  type: 'text' | 'enum';
  required?: boolean;
  max_length?: number;
  label_en?: string;
  label_hu?: string;
  options?: Array<string | DeclarationEnumOption>;
}

export interface DeclarationQuestion {
  key: string;
  type: DeclarationQuestionType;
  required?: boolean;
  instructor_only?: boolean;
  label_en?: string;
  label_hu?: string;
  notice_en?: string;
  notice_hu?: string;
  empty_flag_key?: string;
  empty_flag_label_en?: string;
  empty_flag_label_hu?: string;
  max_length?: number;
  options?: Array<string | DeclarationEnumOption>;
  fields?: DeclarationQuestionField[];
}

export interface DeclarationFormSchema {
  questions: DeclarationQuestion[];
}

export interface DeclarationFormRecord {
  id: string;
  group_id: string;
  name: string;
  description?: string | null;
  status: 'draft' | 'published' | 'archived';
  schema: DeclarationFormSchema;
  version: number;
  is_default?: boolean;
  linked_placement_count?: number;
  submission_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface PlacementDeclarationRecord {
  id: string;
  student_id?: string;
  answers: PlacementDeclarationAnswers;
  created_at: string;
  full_name?: string;
  email?: string;
  neptun_code?: string | null;
  placement_id?: string;
  form_id?: string | null;
  form_version?: number | null;
  form_snapshot?: { id?: string | null; name?: string; version?: number; schema?: DeclarationFormSchema } | null;
}

export interface PlacementTemplateQuestion {
  order: number;
  topic_area: number;
  points: number;
  format: 'numeric' | 'verbal' | 'numeric_with_followup';
}

export interface PlacementTemplate {
  id: string;
  name: string;
  description: string | null;
  language: string;
  total_points: number | string;
  question_count: number;
  questions: PlacementTemplateQuestion[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlacementRecord {
  id: string;
  group_id: string;
  name: string;
  code: string;
  description?: string | null;
  template_id?: string | null;
  template_name?: string | null;
  template_is_active?: boolean | null;
  declaration_form_id?: string | null;
  status: 'draft' | 'open' | 'closed' | 'archived';
  /** hybrid | part_a | part_b — which parts count for ranking and candidate UX */
  assessment_mode?: 'hybrid' | 'part_a' | 'part_b';
  part_a_max_points?: number | null;
  part_b_max_points?: number | null;
  opens_at?: string | null;
  closes_at?: string | null;
  enrolled_count?: number;
  completed_count?: number;
  interview_count?: number;
  section_count?: number;
  created_at?: string;
  updated_at?: string;
}

/** Rows destroyed (or, for `interviews`, merely unlinked) when a placement is deleted. */
export interface PlacementDependencyCounts {
  results: number;
  question_sets: number;
  declarations: number;
  academic_outcomes: number;
  enrollments: number;
  section_links: number;
  interviews: number;
}

export type PlacementBlockingDependency =
  | 'results'
  | 'question_sets'
  | 'declarations'
  | 'academic_outcomes';

export interface PlacementDependencyReport {
  placement_id: string;
  counts: PlacementDependencyCounts;
  blocking: PlacementBlockingDependency[];
  has_assessment_data: boolean;
  can_force: boolean;
}

/** Body of the 409 returned when a placement still holds assessment evidence. */
export interface PlacementDeleteConflictDetails {
  counts: PlacementDependencyCounts;
  blocking: PlacementBlockingDependency[];
}

/** Editing the scoring shape does not rescore stored results; the API says how many went stale. */
export interface PlacementUpdateWarning {
  code: 'STALE_RESULTS';
  affected_results: number;
}

export interface PlacementRankingRow {
  rank: number;
  student_id: string;
  full_name: string;
  email: string;
  neptun_code: string | null;
  final_score: number | null;
  max_score: number;
  // Combined MCQ (Part A) + oral (Part B) score breakdown.
  mcq_score: number | null;
  mcq_max_score: number | null;
  oral_score: number | null;
  oral_max_score: number | null;
  completed: boolean;
  completed_at: string | null;
  integrity_score: number;
  proctoring_flags: number;
  accepts_course: boolean | null;
  accepts_advanced_group: boolean | null;
  declaration_answers: PlacementDeclarationAnswers | null;
  percentile: number;
}

export type PlacementTransferComponent = 'part_a' | 'part_b';
export type PlacementTransferConflictMode = 'override' | 'fill_missing';

export interface PlacementResultTransferScoreSnapshot {
  mcq_score: number | null;
  mcq_max_score: number | null;
  oral_score: number | null;
  oral_max_score: number | null;
  final_score: number | null;
  max_score: number | null;
}

export interface PlacementResultTransferStudent {
  student_id: string;
  full_name: string | null;
  email: string | null;
  neptun_code: string | null;
  will_apply?: boolean;
  applied?: boolean;
  skip_reason: string | null;
  before: PlacementResultTransferScoreSnapshot;
  after: PlacementResultTransferScoreSnapshot;
}

export interface PlacementResultTransferPreview {
  source: { id: string; name: string; code: string; assessment_mode: string };
  target: { id: string; name: string; code: string; assessment_mode: string };
  components: PlacementTransferComponent[];
  conflict_mode: PlacementTransferConflictMode;
  applied_count: number;
  skipped_count: number;
  students: PlacementResultTransferStudent[];
}

export type PlacementResultTransferResult = PlacementResultTransferPreview & {
  transfer_id?: string;
};

export interface PlacementRubricDimension {
  id: string;
  label_hu: string;
  max_points: number;
  awarded_points: number;
  note_hu?: string;
}

export interface PlacementOverrideHistoryEntry {
  timestamp: string;
  reviewer_id: string;
  reviewer_name: string;
  previous_awarded_points: number;
  new_awarded_points: number;
  previous_justification_hu?: string;
  justification_hu: string;
  previous_rubric_dimensions?: PlacementRubricDimension[] | null;
  rubric_dimensions?: PlacementRubricDimension[] | null;
}

export interface PlacementQuestionScore {
  order: number;
  topic_area?: number;
  max_points: number;
  awarded_points: number;
  auto_scored?: boolean;
  extracted_answer?: string | null;
  justification_hu?: string | null;
  relay_suspicion?: boolean;
  adjusted_by?: string | null;
  adjusted_at?: string | null;
  confidence?: number | null;
  evidence_quotes?: string[];
  rubric_dimensions?: PlacementRubricDimension[];
  rubric_hu_snapshot?: string | null;
  override_history?: PlacementOverrideHistoryEntry[];
}

export interface PlacementStudentResult {
  id: string;
  student_id: string;
  full_name: string;
  email: string;
  neptun_code: string | null;
  placement_id: string;
  final_score: number | null;
  max_score: number | null;
  mcq_score: number | null;
  mcq_max_score: number | null;
  oral_score: number | null;
  oral_max_score: number | null;
  completed: boolean;
  completed_at: string | null;
  question_scores: PlacementQuestionScore[];
}

export interface PlacementFairnessReport {
  placement_id: string;
  population: {
    enrolled: number;
    completed: number;
    completion_rate: number;
  };
  score_distribution: {
    n: number;
    mean: number | null;
    median: number | null;
    p25: number | null;
    p75: number | null;
    min: number | null;
    max: number | null;
    histogram: Array<{ from: number; to: number; count: number }>;
  };
  slices: Array<{
    id: string;
    label: string;
    unavailable?: boolean;
    reason?: string;
    groups: Array<{
      key: string;
      n: number;
      completed: number;
      completion_rate: number;
      mean_score: number | null;
      median_score: number | null;
      integrity_flag_rate: number;
      proctoring_flag_rate: number;
    }>;
  }>;
  topic_gaps: Array<{
    topic_area: number;
    label: string;
    n: number;
    mean_awarded: number | null;
    mean_max: number | null;
    mean_pct: number | null;
  }>;
  alerts: Array<{
    severity: string;
    slice: string;
    code: string;
    message: string;
  }>;
  unavailable_slices: Array<{ id: string; label: string; reason: string }>;
}

export interface PlacementOutcomeAnalytics {
  placement_id: string;
  n_outcomes: number;
  analyses: Record<string, {
    n_outcomes: number;
    n_paired: number;
    missingness: number | null;
    pearson: number | null;
    spearman: number | null;
    mean_by_quartile: Array<{
      band: string;
      min: number;
      max: number;
      n: number;
      mean_outcome: number | null;
      mean_placement_score: number | null;
    }>;
    retention_by_band: Array<{
      band: string;
      n: number;
      retention_rate: number | null;
    }> | null;
  }>;
  notes: string[];
  sample_rows: Array<{
    student_id: string;
    full_name: string;
    neptun_code: string | null;
    term_code: string;
    outcome_type: string;
    numeric_value: number | null;
    categorical_value: string | null;
    final_score: number | null;
    completed: boolean;
  }>;
}

export interface PlacementImportSummary {
  batch_id: string;
  total_question_nodes: number;
  imported: number;
  skipped_declaration: number;
  skipped_category_nodes: number;
  skipped_unknown_type: number;
  unsupported_interactive: number;
  images_stored: number;
  by_type: Record<string, number>;
  by_topic: Record<string, number>;
  unmapped_families: string[];
  errors: Array<{ name: string; qtype: string; error: string }>;
}

export interface PlacementRosterImportResult {
  created: number;
  skipped: number;
  errors: Array<{ row: number; name: string; email: string; issues: string[] }>;
  invitations_dispatched: number;
  total_rows: number;
}

export interface ReferenceQuestionRecord {
  id: string;
  year: number | null;
  family_key: string;
  category_path: string;
  name: string | null;
  qtype: string;
  question_html: string;
  answers_jsonb: Record<string, unknown>;
  points: number | string;
  images: Array<{ filename: string; url: string; mime: string; bytes: number }>;
  topic_area: number | null;
  unsupported_interactive: boolean;
  import_batch_id?: string | null;
  created_at: string;
}

export type ReferenceBankExportFormat = 'json' | 'csv' | 'xml' | 'zip-json' | 'zip-csv' | 'zip-xml';

export interface ReferenceQuestionListResponse {
  total: number;
  unsupported: number;
  limit?: number;
  offset?: number;
  questions: ReferenceQuestionRecord[];
  stats?: {
    total: number;
    unsupported: number;
    batch_count: number;
    qtype_count: number;
    last_imported_at: string | null;
    by_topic: Array<{ topic_area: number; count: number }>;
    by_type: Array<{ qtype: string; count: number }>;
  };
}

export const placementApi = {
  getMyResult: async (params: { interviewId?: string; placementId?: string }) => {
    const query = new URLSearchParams();
    if (params.interviewId) query.set('interview_id', params.interviewId);
    if (params.placementId) query.set('placement_id', params.placementId);
    const qs = query.toString();
    return apiRequest<{
      results_hidden: boolean;
      result: AssessmentPlacementResult | null;
    }>(`/placement/me/result${qs ? `?${qs}` : ''}`);
  },

  getTopics: async () => {
    return apiRequest<{ topics: Record<string, { hu: string; en: string }> }>('/placement/topics');
  },

  submitDeclaration: async (answers: PlacementDeclarationAnswers, interviewId?: string) => {
    return apiRequest<{ declaration: PlacementDeclarationRecord }>('/placement/declaration', {
      method: 'POST',
      body: JSON.stringify({ answers, interview_id: interviewId || undefined })
    });
  },

  getDeclarationForm: async (interviewId?: string) => {
    const query = interviewId ? `?interview_id=${encodeURIComponent(interviewId)}` : '';
    return apiRequest<{
      form: DeclarationFormRecord | null;
      required: boolean;
      placement?: { id: string; name: string; status: string } | null;
    }>(`/placement/declaration/form${query}`);
  },

  getMyDeclaration: async (interviewId?: string) => {
    const query = interviewId
      ? `?interview_id=${encodeURIComponent(interviewId)}`
      : '';
    return apiRequest<{
      declaration: PlacementDeclarationRecord | null;
      submitted: boolean;
      required?: boolean;
      form?: DeclarationFormRecord | null;
      placement?: { id: string; name: string; status: string } | null;
    }>(
      `/placement/declaration/me${query}`
    );
  },

  getStudentDeclaration: async (studentId: string, placementId?: string) => {
    const query = placementId ? `?placement_id=${encodeURIComponent(placementId)}` : '';
    return apiRequest<{ declaration: PlacementDeclarationRecord }>(
      `/placement/declarations/${studentId}${query}`
    );
  },

  listDeclarationForms: async () => {
    return apiRequest<{ forms: DeclarationFormRecord[] }>('/placement/declaration-forms');
  },

  getDeclarationFormAdmin: async (id: string) => {
    return apiRequest<{
      form: DeclarationFormRecord;
      placements: Array<{ id: string; name: string; code: string; status: string }>;
    }>(`/placement/declaration-forms/${id}`);
  },

  createDeclarationForm: async (data: {
    name: string;
    description?: string | null;
    schema?: DeclarationFormSchema;
    status?: DeclarationFormRecord['status'];
    is_default?: boolean;
  }) => {
    return apiRequest<{ form: DeclarationFormRecord }>('/placement/declaration-forms', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateDeclarationForm: async (id: string, data: Partial<{
    name: string;
    description: string | null;
    schema: DeclarationFormSchema;
    status: DeclarationFormRecord['status'];
    is_default: boolean;
  }>) => {
    return apiRequest<{ form: DeclarationFormRecord }>(`/placement/declaration-forms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  duplicateDeclarationForm: async (id: string) => {
    return apiRequest<{ form: DeclarationFormRecord }>(`/placement/declaration-forms/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  linkDeclarationForm: async (id: string, placementIds: string[]) => {
    return apiRequest<{ form: DeclarationFormRecord; updated: Array<{ id: string; name: string }> }>(
      `/placement/declaration-forms/${id}/link`,
      { method: 'POST', body: JSON.stringify({ placement_ids: placementIds }) }
    );
  },

  unlinkDeclarationForm: async (id: string, placementIds: string[]) => {
    return apiRequest<{ form: DeclarationFormRecord; updated: Array<{ id: string; name: string }> }>(
      `/placement/declaration-forms/${id}/unlink`,
      { method: 'POST', body: JSON.stringify({ placement_ids: placementIds }) }
    );
  },

  archiveDeclarationForm: async (id: string) => {
    return apiRequest<{ message: string; form: DeclarationFormRecord }>(
      `/placement/declaration-forms/${id}`,
      { method: 'DELETE' }
    );
  },

  listTemplates: async () => {
    return apiRequest<{ templates: PlacementTemplate[] }>('/placement/templates');
  },

  createTemplate: async (data: {
    name: string;
    description?: string;
    questions: PlacementTemplateQuestion[];
    is_active?: boolean;
  }) => {
    return apiRequest<{ template: PlacementTemplate }>('/placement/templates', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateTemplate: async (id: string, data: {
    name?: string;
    description?: string;
    questions?: PlacementTemplateQuestion[];
    is_active?: boolean;
  }) => {
    return apiRequest<{ template: PlacementTemplate }>(`/placement/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  generateQuestionSet: async (data?: {
    student_id?: string;
    session_id?: string;
    interview_id?: string;
    placement_id?: string;
  }) => {
    return apiRequest<{ question_set: { id: string; questions: PlacementGeneratedQuestion[] }; reused: boolean }>(
      '/placement/question-sets/generate',
      { method: 'POST', body: JSON.stringify(data || {}) }
    );
  },

  // Part B adaptive follow-up (combined MCQ + oral assessment): generate the
  // verbal follow-up dynamically from the candidate's actual answer instead
  // of the statically pre-generated followup_hu, bounded to a difficulty
  // range around the question's baseline.
  getAdaptiveFollowup: async (setId: string, order: number, data: {
    session_id?: string;
    answer_text: string;
    difficulty_step?: number;
    language?: string;
    /** Recently spoken acknowledgements, so the examiner does not repeat itself. */
    recent_acknowledgements?: string[];
  }, options:{signal?:AbortSignal}={}) => {
    return apiRequest<{
      followup_hu: string | null;
      generated: boolean;
      probe_type?: string | null;
      integrity_probe?: boolean;
      /** Neutral "I heard you" line spoken before the follow-up. */
      acknowledgement?: string | null;
      /** Neutral "here is where we go next" line spoken before the follow-up. */
      bridge?: string | null;
    }>(
      `/placement/question-sets/${setId}/questions/${order}/adaptive-followup`,
      { method: 'POST', body: JSON.stringify(data), signal:options.signal }
    );
  },

  importReferenceQuestions: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiUpload<{ summary: PlacementImportSummary }>('/placement/reference-questions/import', formData);
  },

  listReferenceQuestions: async (params?: {
    topic_area?: number;
    qtype?: string;
    family?: string;
    import_batch_id?: string;
    q?: string;
    unsupported?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.topic_area) query.set('topic_area', String(params.topic_area));
    if (params?.qtype) query.set('qtype', params.qtype);
    if (params?.family) query.set('family', params.family);
    if (params?.import_batch_id) query.set('import_batch_id', params.import_batch_id);
    if (params?.q) query.set('q', params.q);
    if (params?.unsupported !== undefined) query.set('unsupported', String(params.unsupported));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return apiRequest<ReferenceQuestionListResponse>(
      `/placement/reference-questions${qs ? `?${qs}` : ''}`
    );
  },

  exportReferenceQuestions: async (params?: {
    format?: ReferenceBankExportFormat;
    topic_area?: number;
    qtype?: string;
    family?: string;
    import_batch_id?: string;
    q?: string;
    limit?: number;
    ids?: string[];
  }) => {
    if (params?.ids?.length) {
      return apiRequestBlob('/placement/reference-questions/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: params.format || 'json',
          ids: params.ids
        })
      });
    }
    const query = new URLSearchParams();
    query.set('format', params?.format || 'json');
    if (params?.topic_area) query.set('topic_area', String(params.topic_area));
    if (params?.qtype) query.set('qtype', params.qtype);
    if (params?.family) query.set('family', params.family);
    if (params?.import_batch_id) query.set('import_batch_id', params.import_batch_id);
    if (params?.q) query.set('q', params.q);
    if (params?.limit) query.set('limit', String(params.limit));
    return apiRequestBlob(`/placement/reference-questions/export?${query.toString()}`);
  },

  remapReferenceQuestionTopics: async (ids: string[], topicArea: number | null) => {
    return apiRequest<{ updated: number; topic_area: number | null }>('/placement/reference-questions/topic', {
      method: 'PATCH',
      body: JSON.stringify({ ids, topic_area: topicArea })
    });
  },

  deleteReferenceQuestion: async (id: string) => {
    return apiRequest<{ deleted: number; id: string }>(
      `/placement/reference-questions/${encodeURIComponent(id)}?confirm=DELETE`,
      { method: 'DELETE' }
    );
  },

  deleteReferenceQuestionBatch: async (batchId: string) => {
    return apiRequest<{ deleted: number; batch_id: string }>(
      `/placement/reference-questions/batch/${encodeURIComponent(batchId)}?confirm=DELETE`,
      { method: 'DELETE' }
    );
  },

  getRanking: async (placementId: string) => {
    return apiRequest<{ ranking: PlacementRankingRow[]; total: number; placement: PlacementRecord }>(
      `/placement/ranking?placement_id=${encodeURIComponent(placementId)}`
    );
  },

  exportRanking: async (format: 'csv' | 'xlsx', placementId: string) => {
    return apiRequestBlob(`/placement/ranking/export?format=${format}&placement_id=${encodeURIComponent(placementId)}`);
  },

  adjustScore: async (studentId: string, data: {
    placement_id: string;
    question_order: number;
    awarded_points: number;
    justification: string;
    rubric_dimensions?: Array<{
      id: string;
      label_hu: string;
      max_points: number;
      awarded_points: number;
      note_hu?: string;
    }>;
  }) => {
    return apiRequest<{ result: unknown }>(`/placement/results/${studentId}/scores`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  getStudentResult: async (studentId: string, placementId: string) => {
    return apiRequest<{ result: PlacementStudentResult }>(
      `/placement/results/${studentId}?placement_id=${encodeURIComponent(placementId)}`
    );
  },

  getFairness: async (placementId: string) => {
    return apiRequest<{ fairness: PlacementFairnessReport; placement: PlacementRecord }>(
      `/placement/fairness?placement_id=${encodeURIComponent(placementId)}`
    );
  },

  exportFairness: async (placementId: string, format: 'csv' | 'xlsx' = 'csv') => {
    return apiRequestBlob(
      `/placement/fairness/export?placement_id=${encodeURIComponent(placementId)}&format=${format}`
    );
  },

  getOutcomeAnalytics: async (placementId: string) => {
    return apiRequest<{ analytics: PlacementOutcomeAnalytics; placement: PlacementRecord }>(
      `/placement/outcomes/analytics?placement_id=${encodeURIComponent(placementId)}`
    );
  },

  importOutcomes: async (placementId: string, data: { csv?: string; rows?: Array<Record<string, unknown>> }) => {
    return apiRequest<{
      summary: {
        imported: number;
        updated: number;
        errors: Array<{ row: number; error: string }>;
        total_rows: number;
      };
      placement: PlacementRecord;
    }>(`/placement/outcomes/import?placement_id=${encodeURIComponent(placementId)}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  downloadReportPack: async (placementId: string, options: { format?: 'pdf' | 'xlsx' | 'zip'; cutoff?: number } = {}) => {
    const format = options.format || 'zip';
    const cutoff = options.cutoff ?? 20;
    return apiRequestBlob(
      `/placement/report-pack?placement_id=${encodeURIComponent(placementId)}&format=${format}&cutoff=${cutoff}`
    );
  },

  listPlacements: async () => {
    return apiRequest<{ placements: PlacementRecord[] }>('/placement/placements');
  },

  getPlacement: async (id: string) => {
    return apiRequest<{
      placement: PlacementRecord;
      sections: Array<{ id: string; name: string; description?: string; candidate_count?: number }>;
      interviews: Array<{ id: string; title: string; status: string }>;
    }>(`/placement/placements/${id}`);
  },

  createPlacement: async (data: {
    name: string;
    code?: string;
    description?: string;
    template_id?: string | null;
    status?: PlacementRecord['status'];
    assessment_mode?: PlacementRecord['assessment_mode'];
    part_a_max_points?: number | null;
    part_b_max_points?: number | null;
    opens_at?: string | null;
    closes_at?: string | null;
  }) => {
    return apiRequest<{ placement: PlacementRecord }>('/placement/placements', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateResultVisibility: async (
    placementId: string,
    data: { candidate_can_view_results: boolean }
  ) => {
    return apiRequest<{
      placement_id: string;
      candidate_can_view_results: boolean;
      updated_count: number;
      assignments: Array<{
        id: string;
        candidate_id: string;
        interview_id: string;
        status: string;
        candidate_can_view_results: boolean;
        updated_at: string;
      }>;
    }>(`/placement/placements/${placementId}/result-visibility`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  previewResultTransfer: async (
    targetPlacementId: string,
    data: {
      source_placement_id: string;
      student_ids: string[];
      components: PlacementTransferComponent[];
      conflict_mode: PlacementTransferConflictMode;
    }
  ) => {
    return apiRequest<PlacementResultTransferPreview>(
      `/placement/placements/${targetPlacementId}/transfer-results/preview`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  },

  transferResults: async (
    targetPlacementId: string,
    data: {
      source_placement_id: string;
      student_ids: string[];
      components: PlacementTransferComponent[];
      conflict_mode: PlacementTransferConflictMode;
      reason: string;
    }
  ) => {
    return apiRequest<PlacementResultTransferResult>(
      `/placement/placements/${targetPlacementId}/transfer-results`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  },

  updatePlacement: async (id: string, data: Partial<{
    name: string;
    code: string;
    description: string | null;
    template_id: string | null;
    status: PlacementRecord['status'];
    assessment_mode: PlacementRecord['assessment_mode'];
    part_a_max_points: number | null;
    part_b_max_points: number | null;
    opens_at: string | null;
    closes_at: string | null;
    declaration_form_id: string | null;
  }>) => {
    return apiRequest<{
      placement: PlacementRecord;
      changed_fields?: string[];
      warnings?: PlacementUpdateWarning[];
    }>(`/placement/placements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  getPlacementDependencies: async (id: string) => {
    return apiRequest<PlacementDependencyReport>(`/placement/placements/${id}/dependencies`);
  },

  deletePlacement: async (id: string, options: { force?: boolean } = {}) => {
    const query = options.force ? '?confirm=DELETE&force=true' : '?confirm=DELETE';
    return apiRequest<{
      message: string;
      placement_id: string;
      destroyed: PlacementDependencyCounts;
      unlinked_interviews: number;
    }>(`/placement/placements/${id}${query}`, { method: 'DELETE' });
  },

  duplicatePlacement: async (id: string) => {
    return apiRequest<{ placement: PlacementRecord }>(`/placement/placements/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  enrolPlacement: async (id: string, data: { candidate_ids?: string[]; section_ids?: string[] }) => {
    return apiRequest<{ ok: boolean; outcomes: unknown }>(`/placement/placements/${id}/enrol`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  linkInterviewToPlacement: async (id: string, interviewId: string) => {
    return apiRequest<{ interview: { id: string; title: string; placement_id: string } }>(
      `/placement/placements/${id}/link-interview`,
      { method: 'POST', body: JSON.stringify({ interview_id: interviewId }) }
    );
  },

  importStudents: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiUpload<PlacementRosterImportResult>('/placement/students/import', formData);
  },

  resetAttempt: async (studentId: string, placementId: string, reason?: string) => {
    return apiRequest<{ success: boolean }>(`/placement/students/${studentId}/reset-attempt`, {
      method: 'POST',
      body: JSON.stringify({ placement_id: placementId, reason })
    });
  },

  exportStudentData: async (studentId: string) => {
    return apiRequest<Record<string, unknown>>(`/placement/students/${studentId}/export`);
  },

  /** @deprecated Submits/reuses an erasure review (202), never immediate deletion. Review in Privacy Center. */
  deleteStudentData: async (studentId: string, reason?: string) => {
    return apiRequest<{
      success: false;
      erased: false;
      erasure_requested: true;
      reused: boolean;
      code: 'PRIVACY_ERASURE_REVIEW_REQUESTED';
      privacy_center_path: string;
      request: { id: string; subject_user_id: string; request_type: 'erase'; status: string; created_at: string };
    }>(`/placement/students/${studentId}/data`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm: true, reason })
    });
  },

  getRetentionSetting: async () => {
    return apiRequest<{ retention_days: number }>('/placement/settings/retention');
  },

  updateRetentionSetting: async (days: number) => {
    return apiRequest<{ retention_days: number }>('/placement/settings/retention', {
      method: 'PUT',
      body: JSON.stringify({ retention_days: days })
    });
  }
};

// =============================================================================
// QUALITY CONTROLS API
// =============================================================================
export interface QualityControlConfig {
  id: string;
  rubric_version: number;
  prompt_version: number;
  confidence_threshold?: number | null;
  require_human_review_below_threshold?: boolean;
  rubric_definition?: Record<string, unknown> | null;
  prompt_template?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface QualityControlCalibrationDataset {
  id: string;
  name: string;
  description?: string | null;
  version?: number;
  sample_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface QualityControlCalibrationSample {
  id: string;
  assessment_id: string;
  expected_score?: number | null;
  expected_decision?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface QualityControlCalibrationReportRow {
  assessment_id: string;
  actual_score: number | null;
  expected_score: number | null;
  score_delta: number | null;
  actual_decision: string | null;
  expected_decision: string | null;
  decision_match: boolean | null;
}

export const qualityControlsApi = {
  listConfigs: async () => {
    return apiRequest<{ configs: QualityControlConfig[] }>('/quality-controls/configs');
  },

  saveConfig: async (data: {
    rubric_version: number;
    prompt_version: number;
    confidence_threshold?: number;
    require_human_review_below_threshold?: boolean;
    rubric_definition?: Record<string, unknown>;
    prompt_template?: string;
    is_active?: boolean;
  }) => {
    return apiRequest<{ config: QualityControlConfig }>('/quality-controls/configs', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  activateConfig: async (id: string) => {
    return apiRequest<{ config: QualityControlConfig }>(`/quality-controls/configs/${id}/activate`, {
      method: 'POST'
    });
  },

  resetConfig: async () => {
    return apiRequest<{
      config: QualityControlConfig | null;
      deactivated_count: number;
      message?: string;
    }>('/quality-controls/configs/reset', {
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  listCalibrationDatasets: async () => {
    return apiRequest<{ datasets: QualityControlCalibrationDataset[] }>('/quality-controls/calibration-datasets');
  },

  createCalibrationDataset: async (data: { name: string; description?: string; version?: number }) => {
    return apiRequest<{ dataset: QualityControlCalibrationDataset }>('/quality-controls/calibration-datasets', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  listCalibrationSamples: async (datasetId: string) => {
    return apiRequest<{ samples: QualityControlCalibrationSample[] }>(`/quality-controls/calibration-datasets/${datasetId}/samples`);
  },

  createCalibrationSample: async (datasetId: string, data: {
    assessment_id: string;
    expected_score?: number;
    expected_decision?: string;
    notes?: string;
  }) => {
    return apiRequest<{ sample: QualityControlCalibrationSample }>(`/quality-controls/calibration-datasets/${datasetId}/samples`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getCalibrationReport: async (datasetId: string) => {
    return apiRequest<{
      dataset_id: string;
      sample_count: number;
      average_score_delta: number;
      decision_match_rate: number | null;
      rows: QualityControlCalibrationReportRow[];
    }>(`/quality-controls/calibration-datasets/${datasetId}/report`);
  }
};

export interface TranscriptSessionSummary {
  session_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  interview_id: string;
  interview_title: string;
  interview_type: string;
  status: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  duration_seconds?: number | null;
  integrity_score?: number | null;
  transcript_entries_count?: number;
}

export interface TranscriptPagination {
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
}

export interface TranscriptExchange {
  exchange_number: number;
  question: string;
  answer: string;
  question_type: string;
  timestamp?: string | null;
  question_timestamp?: string | null;
  answer_timestamp?: string | null;
  response_duration_seconds?: number;
  ai_feedback?: string | null;
  integrity_score?: number | null;
  integrity_events?: Array<Record<string, any>>;
  mcq_options?: string[] | null;
  selected_option?: string | null;
  correct_option?: string | null;
  is_correct?: boolean | null;
}

export interface TranscriptSessionDetail {
  session_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  interview_id: string;
  interview_title: string;
  interview_type: string;
  status: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  duration_seconds?: number | null;
  integrity_score?: number | null;
  integrity_events?: Array<Record<string, any>>;
  transcript_summary: {
    total_exchanges: number;
    average_response_time: number;
  };
  qa_exchanges: TranscriptExchange[];
}

export const transcriptsApi = {
  listSessions: async (params?: {
    status?: string;
    candidate_id?: string;
    interview_id?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        queryParams.set(key, String(value));
      });
    }

    const query = queryParams.toString();
    return apiRequest<{
      sessions: TranscriptSessionSummary[];
      pagination: TranscriptPagination;
    }>(`/transcripts/sessions${query ? `?${query}` : ''}`);
  },

  getSessionDetail: async (sessionId: string) => {
    return apiRequest<{ session: TranscriptSessionDetail }>(`/transcripts/sessions/${sessionId}`);
  },

  listByInterview: async (interviewId: string, params?: {
    status?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        queryParams.set(key, String(value));
      });
    }

    const query = queryParams.toString();
    return apiRequest<{
      interview_id: string;
      sessions: TranscriptSessionSummary[];
      pagination: TranscriptPagination;
    }>(`/transcripts/interviews/${interviewId}${query ? `?${query}` : ''}`);
  },

  listByCandidate: async (candidateId: string, params?: {
    status?: string;
    interview_id?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        queryParams.set(key, String(value));
      });
    }

    const query = queryParams.toString();
    return apiRequest<{
      candidate_id: string;
      sessions: TranscriptSessionSummary[];
      pagination: TranscriptPagination;
    }>(`/transcripts/candidates/${candidateId}${query ? `?${query}` : ''}`);
  },

  searchSessions: async (params: {
    query: string;
    status?: string;
    candidate_id?: string;
    interview_id?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      queryParams.set(key, String(value));
    });

    const query = queryParams.toString();
    return apiRequest<{
      query: string;
      sessions: TranscriptSessionSummary[];
      pagination: TranscriptPagination;
    }>(`/transcripts/search${query ? `?${query}` : ''}`);
  }
};

// =============================================================================
// HEALTH CHECK
// =============================================================================
export type UserGuideAudience = 'candidate' | 'invigilator' | 'admin' | 'super_admin';

export interface UserGuideSummary {
  id: string;
  slug: string;
  audience_role: UserGuideAudience;
  module_key?: string | null;
  locale: string;
  title: string;
  summary?: string;
  published: boolean;
  sort_order: number;
  step_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface UserGuideStep {
  id: string;
  sort_order: number;
  title: string;
  body: string;
  has_image: boolean;
  image_original_name?: string | null;
}

export interface UserGuideDetail {
  guide: UserGuideSummary;
  steps: UserGuideStep[];
}

export const USER_GUIDE_MODULE_KEYS = [
  'admin.overview',
  'admin.interviews',
  'admin.materials',
  'admin.candidates',
  'admin.sections',
  'admin.placement',
  'admin.live-monitoring',
  'admin.accounts',
  'admin.mcq-sets',
  'admin.assessments',
  'admin.llm-settings',
  'admin.voice-studio',
  'admin.privacy',
  'candidate.overview',
  'invigilator.overview',
  'invigilator.live-av',
  'invigilator.report-history'
] as const;

async function fetchAuthorizedBlob(endpoint: string): Promise<Blob> {
  const url = `${API_URL}/api${endpoint}`;
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (activeGroupId) headers['x-group-id'] = activeGroupId;
  applyLocaleHeader(headers);

  let response = await fetch(url, { headers });
  if (response.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed && accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
      response = await fetch(url, { headers });
    }
  }
  if (!response.ok) {
    throw new Error('Failed to load guide image');
  }
  return response.blob();
}

export const userGuidesApi = {
  list: async (params?: { locale?: string; module?: string }) => {
    const query = new URLSearchParams();
    if (params?.locale) query.set('locale', params.locale);
    if (params?.module) query.set('module', params.module);
    const qs = query.toString();
    return apiRequest<{ locale: string; guides: UserGuideSummary[] }>(qs ? `/user-guides?${qs}` : '/user-guides');
  },

  get: async (idOrSlug: string, locale?: string) => {
    const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return apiRequest<UserGuideDetail>(`/user-guides/${encodeURIComponent(idOrSlug)}${query}`);
  },

  getStepImageBlob: async (guideId: string, stepId: string) => {
    return fetchAuthorizedBlob(`/user-guides/${guideId}/steps/${stepId}/image`);
  },

  adminList: async (params?: { audience_role?: string; locale?: string; published?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.audience_role) query.set('audience_role', params.audience_role);
    if (params?.locale) query.set('locale', params.locale);
    if (params?.published) query.set('published', params.published);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return apiRequest<{ guides: UserGuideSummary[] }>(qs ? `/admin/user-guides?${qs}` : '/admin/user-guides');
  },

  adminGet: async (id: string) => {
    return apiRequest<UserGuideDetail>(`/admin/user-guides/${id}`);
  },

  adminCreate: async (data: {
    slug: string;
    audience_role: UserGuideAudience;
    locale: string;
    title: string;
    summary?: string;
    module_key?: string | null;
    published?: boolean;
    sort_order?: number;
  }) => {
    return apiRequest<UserGuideDetail>('/admin/user-guides', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  adminUpdate: async (id: string, data: Partial<{
    slug: string;
    audience_role: UserGuideAudience;
    locale: string;
    title: string;
    summary: string | null;
    module_key: string | null;
    published: boolean;
    sort_order: number;
  }>) => {
    return apiRequest<{ guide: UserGuideSummary }>(`/admin/user-guides/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  adminRemove: async (id: string) => {
    return apiRequest<{ message: string }>(`/admin/user-guides/${id}`, { method: 'DELETE' });
  },

  adminSaveSteps: async (id: string, steps: Array<{ id?: string; title: string; body: string; sort_order?: number }>) => {
    return apiRequest<{ steps: UserGuideStep[] }>(`/admin/user-guides/${id}/steps`, {
      method: 'PUT',
      body: JSON.stringify({ steps })
    });
  },

  adminDuplicate: async (id: string, locale: string) => {
    return apiRequest<UserGuideDetail>(`/admin/user-guides/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ locale })
    });
  },

  adminUploadStepImage: async (guideId: string, stepId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiUpload<{ step: UserGuideStep }>(`/admin/user-guides/${guideId}/steps/${stepId}/image`, formData);
  },

  adminRemoveStepImage: async (guideId: string, stepId: string) => {
    return apiRequest<{ step: UserGuideStep }>(`/admin/user-guides/${guideId}/steps/${stepId}/image`, {
      method: 'DELETE'
    });
  }
};

export type PrivacyRequestType = 'access' | 'export' | 'rectify' | 'erase' | 'restrict' | 'object' | 'do_not_sell';
export type PrivacyRequestStatus = 'received' | 'in_progress' | 'waiting_on_subject' | 'completed' | 'rejected' | 'cancelled';

export interface PrivacyNotice {
  id?: string;
  slug: string;
  locale: string;
  title: string;
  body: string;
  version: number;
  published?: boolean;
  effective_at?: string | null;
  acknowledged?: boolean;
  acknowledged_at?: string | null;
}

export interface PrivacyRequestRow {
  id: string;
  request_type: PrivacyRequestType;
  status: PrivacyRequestStatus;
  jurisdiction?: string | null;
  notes?: string | null;
  denial_reason?: string | null;
  due_at?: string | null;
  created_at: string;
  closed_at?: string | null;
  subject_user_id?: string;
  subject_email?: string;
  subject_name?: string;
  subject_role?: string;
  export_file_path?: string | null;
  export_state?: 'captured' | 'ready' | 'expired' | 'erased' | null;
  export_audience?: 'subject' | 'staff' | null;
  origin_group_id?: string | null;
  execution_status?: 'executing' | 'reconciliation_required' | 'completed' | null;
}

export interface PrivacySettings {
  evidence_retention_days: number;
  recording_retention_days: number;
  transcript_retention_days: number;
  audit_log_retention_days: number;
  export_retention_days: number;
  lawful_basis_proctoring: string;
  do_not_sell: boolean;
  require_privacy_ack_on_login: boolean;
  erase_mode: 'anonymize_keep_scores' | 'hard_delete';
  export_include_evidence: boolean;
  dpo_contact_email?: string | null;
  jurisdictions: string[];
  transfer_note?: string | null;
}

export interface PrivacyErasureExecution {
  request_id: string;
  subject_user_id: string;
  actor_user_id: string;
  status: 'executing' | 'reconciliation_required' | 'completed';
  outcome: { mode: string } | null;
  last_code: string | null;
  created_at: string;
  finished_at: string | null;
  data_erasure_committed?: boolean;
  files_pending?: number;
  files_blocked?: number;
}

export interface TranscriptExpiryReview {
  id: string;
  session_id: string;
  subject_user_id: string;
  interview_id: string;
  group_id: string;
  status: 'queued' | 'retained' | 'purged';
  revision: number;
  retention_days: number;
  source_summary: { transcript_entries: number | null; resume_snapshot_present: boolean; autosave_records: number; history_records: number; source_bytes: number };
  subject_name?: string | null;
  interview_title?: string | null;
  reviewed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlockedObjectReview {
  object_id: string;
  source: 'erasure_file' | 'evidence_file' | 'legacy_export_file' | 'avatar_file' | 'material_file';
  kind: string;
  last_code: string | null;
  outcome: 'negative' | 'unknown';
  attempts: number;
  created_at: string;
  subject_user_id: string | null;
  subject_name?: string | null;
  held: boolean;
}

export const privacyApi = {
  adminTranscriptExpiryReviews: (params?: { after?: string; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.after) query.set('after', params.after);
    if (params?.status) query.set('status', params.status);
    return apiRequest<{ reviews: TranscriptExpiryReview[]; next_cursor: string | null }>(`/admin/privacy/transcript-expiry-reviews${query.size ? `?${query}` : ''}`, { suppressGlobalErrorRedirect: true });
  },
  adminDecideTranscriptExpiry: (id: string, input: { action: 'refresh' | 'retain' | 'purge'; expected_revision: number; reason?: string; confirm?: true }) =>
    apiRequest<{ review_id: string; status: 'queued' | 'retained' | 'purged'; revision?: number; reused: boolean; outcome?: { export_snapshots_expired: number; export_cache_cleanup_separate: boolean } }>(
      `/admin/privacy/transcript-expiry-reviews/${id}/decision`, { method: 'POST', body: JSON.stringify(input), suppressGlobalErrorRedirect: true }
    ),
  adminBlockedObjects: (params?: { after?: string }) => {
    const query = new URLSearchParams();
    if (params?.after) query.set('after', params.after);
    return apiRequest<{ objects: BlockedObjectReview[]; next_cursor: string | null; automatic_removal: false }>(`/admin/privacy/blocked-objects${query.size ? `?${query}` : ''}`, { suppressGlobalErrorRedirect: true });
  },
  adminRetainBlockedObject: (input: { source: BlockedObjectReview['source']; object_id: string; expected_code: string | null; action: 'retain'; reason: string }) =>
    apiRequest<{ object_id: string; source: string; outcome: 'negative' | 'unknown'; retained: true; reused: boolean; automatic_removal: false }>(
      '/admin/privacy/blocked-objects/decision', { method: 'POST', body: JSON.stringify(input), suppressGlobalErrorRedirect: true }
    ),
  publicNotices: async (locale?: string) => {
    const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    const response = await fetch(`${API_URL}/api/privacy/notices/public${query}`);
    if (!response.ok) {
      throw new Error('Failed to load privacy notice');
    }
    return response.json() as Promise<{
      locale: string;
      do_not_sell: boolean;
      essential_storage_only: boolean;
      notices: PrivacyNotice[];
    }>;
  },

  notices: (locale?: string) => {
    const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return apiRequest<{ locale: string; notices: PrivacyNotice[] }>(`/privacy/notices${query}`);
  },

  acknowledge: (slug: string) =>
    apiRequest<{ acknowledgement: { id: string }; already_acknowledged: boolean }>(
      `/privacy/notices/${encodeURIComponent(slug)}/acknowledge`,
      { method: 'POST' }
    ),

  me: () =>
    apiRequest<{
      locale: string;
      settings: Partial<PrivacySettings> & { do_not_sell?: boolean; require_privacy_ack_on_login?: boolean };
      notices: PrivacyNotice[];
      acknowledgements: Array<{ document_slug: string; document_version: number; accepted_at: string }>;
      requests: PrivacyRequestRow[];
      privacy_notice_acked: boolean;
      require_ack: boolean;
    }>('/privacy/me', { suppressGlobalErrorRedirect: true }),

  myRequests: () => apiRequest<{ requests: PrivacyRequestRow[] }>('/privacy/requests'),

  createRequest: (data: { request_type: PrivacyRequestType; jurisdiction?: string; notes?: string }) =>
    apiRequest<{ request: PrivacyRequestRow; message?: string }>('/privacy/requests', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  exportMine: (requestId?: string) =>
    apiRequest<{ request_id: string; file_name: string; download_path: string }>('/privacy/export', {
      method: 'POST', body: JSON.stringify(requestId ? { request_id: requestId } : {}), suppressGlobalErrorRedirect: true
    }),

  downloadExport: (requestId: string) => apiRequestBlob(`/privacy/requests/${requestId}/export`, { suppressGlobalErrorRedirect: true }),

  adminRequests: (params?: { status?: string; request_type?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.request_type) query.set('request_type', params.request_type);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return apiRequest<{ requests: PrivacyRequestRow[] }>(qs ? `/admin/privacy/requests?${qs}` : '/admin/privacy/requests', { suppressGlobalErrorRedirect: true });
  },

  adminCreateRequest: (data: { subject_user_id: string; request_type: PrivacyRequestType; jurisdiction?: string; notes?: string }) =>
    apiRequest<{ request: PrivacyRequestRow }>('/admin/privacy/requests', {
      method: 'POST',
      body: JSON.stringify(data), suppressGlobalErrorRedirect: true
    }),

  adminPatchRequest: (id: string, data: Partial<{ status: PrivacyRequestStatus; notes: string; denial_reason: string }>) =>
    apiRequest<{ request: PrivacyRequestRow }>(`/admin/privacy/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  adminExport: (id: string) =>
    apiRequest<{ request: PrivacyRequestRow; file_name: string; download_path: string }>(`/admin/privacy/requests/${id}/export`, {
      method: 'POST', suppressGlobalErrorRedirect: true
    }),

  adminDownloadExport: (id: string) => apiRequestBlob(`/admin/privacy/requests/${id}/export`, { suppressGlobalErrorRedirect: true }),

  adminErasureExecutions: () => apiRequest<{ executions: PrivacyErasureExecution[] }>('/admin/privacy/erasure-executions', { suppressGlobalErrorRedirect: true }),

  adminReconcileErasure: (id: string, confirmation: { confirm: true }) => apiRequest<{ outcome: { mode: string }; cleanup_pending?: boolean; reused: boolean }>(
    `/admin/privacy/erasure-executions/${id}/reconcile`, { method: 'POST', body: JSON.stringify(confirmation), suppressGlobalErrorRedirect: true }
  ),

  adminErase: (id: string, confirmation: { confirm: true; forceHardDelete?: boolean }) =>
    apiRequest<{ request: PrivacyRequestRow | null; outcome: { mode: string }; reused: boolean }>(`/admin/privacy/requests/${id}/erase`, {
      method: 'POST',
      body: JSON.stringify({ confirm: confirmation.confirm, force_hard_delete: Boolean(confirmation.forceHardDelete) }),
      suppressGlobalErrorRedirect: true
    }),

  adminNotices: (params?: { locale?: string; slug?: string }) => {
    const query = new URLSearchParams();
    if (params?.locale) query.set('locale', params.locale);
    if (params?.slug) query.set('slug', params.slug);
    const qs = query.toString();
    return apiRequest<{ notices: PrivacyNotice[] }>(qs ? `/admin/privacy/notices?${qs}` : '/admin/privacy/notices');
  },

  adminCreateNotice: (data: { slug: string; locale: string; title: string; body: string; published?: boolean }) =>
    apiRequest<{ notice: PrivacyNotice }>('/admin/privacy/notices', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  adminUpdateNotice: (id: string, data: Partial<{ title: string; body: string; published: boolean }>) =>
    apiRequest<{ notice: PrivacyNotice }>(`/admin/privacy/notices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  adminAcknowledgements: (slug?: string) => {
    const query = slug ? `?slug=${encodeURIComponent(slug)}` : '';
    return apiRequest<{ acknowledgements: Array<{ id: string; email: string; full_name: string; document_slug: string; document_version: number; locale: string; accepted_at: string }> }>(`/admin/privacy/acknowledgements${query}`);
  },

  adminSettings: () => apiRequest<{ settings: PrivacySettings }>('/admin/privacy/settings'),

  adminUpdateSettings: (data: Partial<PrivacySettings>) =>
    apiRequest<{ settings: PrivacySettings }>('/admin/privacy/settings', {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  adminSubprocessors: () =>
    apiRequest<{ subprocessors: Array<{ id: string; name: string; purpose: string; region_note?: string; website?: string; active: boolean; sort_order: number }> }>('/admin/privacy/subprocessors'),

  adminCreateSubprocessor: (data: { name: string; purpose: string; region_note?: string; website?: string; active?: boolean }) =>
    apiRequest<{ subprocessor: { id: string } }>('/admin/privacy/subprocessors', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  adminUpdateSubprocessor: (id: string, data: Partial<{ name: string; purpose: string; region_note: string; website: string; active: boolean }>) =>
    apiRequest<{ subprocessor: { id: string } }>(`/admin/privacy/subprocessors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  adminDeleteSubprocessor: (id: string) =>
    apiRequest<{ message: string }>(`/admin/privacy/subprocessors/${id}`, { method: 'DELETE' }),

  adminIncidents: () =>
    apiRequest<{ incidents: Array<{ id: string; title: string; summary?: string; severity: string; status: string; discovered_at: string; notified_authority_at?: string; notified_subjects_at?: string }> }>('/admin/privacy/incidents'),

  adminCreateIncident: (data: { title: string; summary?: string; severity?: string; status?: string }) =>
    apiRequest<{ incident: { id: string } }>('/admin/privacy/incidents', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  adminUpdateIncident: (id: string, data: Partial<{ title: string; summary: string; severity: string; status: string; notified_authority_at: string; notified_subjects_at: string }>) =>
    apiRequest<{ incident: { id: string } }>(`/admin/privacy/incidents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
};

export const healthCheck = async (): Promise<{ status: string; database: string }> => {
  const response = await fetch(`${API_URL}/ready`);
  return response.json();
};

// =============================================================================
// CODE EXECUTION API
// =============================================================================
export const codeApi = {
  execute: (code: string, language: string, test_cases?: { input: string; expected_output: string; is_hidden?: boolean }[]) =>
    apiRequest<{
      success: boolean;
      results: { passed: boolean; input: string; expected: string; actual: string; error?: string }[];
      summary: { total: number; passed: number; failed: number };
      output: string;
      error?: string;
    }>('/execute', {
      method: 'POST',
      body: JSON.stringify({ code, language, test_cases }),
      // A runner outage is recoverable in the editor. Never navigate away from
      // an unsaved answer; authentication refresh still follows the normal path.
      suppressGlobalErrorRedirect: true,
    }),
};

export default {
  auth: authApi,
  groups: groupsApi,
  llmConfig: llmConfigApi,
  users: usersApi,
  interviews: interviewsApi,
  materials: materialsApi,
  materialSections: materialSectionsApi,
  materialLibrary: materialLibraryApi,
  mcq: mcqApi,
  sessions: sessionsApi,
  invigilation: invigilationApi,
  sections: sectionsApi,
  assessments: assessmentsApi,
  feedback: feedbackApi,
  audit: auditApi,
  dashboard: dashboardApi,
  adminTools: adminToolsApi,
  notifications: notificationsApi,
  voiceStudio: voiceStudioApi,
  placement: placementApi,
  qualityControls: qualityControlsApi,
  transcripts: transcriptsApi,
  userGuides: userGuidesApi,
  privacy: privacyApi,
  code: codeApi,
  healthCheck,
};
